import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Button,
  Box,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Skeleton,
  Pagination,
  Select,
  FormControl,
  MenuItem as SelectMenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  InputLabel,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Collapse,
  Tooltip,
  Stack,
  Slider,
} from '@mui/material';
import {
  Refresh,
  Delete,
  Visibility,
  PersonAdd,
  Restore,
  ExpandMore,
  ExpandLess,
  Search,
  Clear,
  VolumeUp,
  Login,
  Logout,
  CalendarMonth,
  Mic,
  CloudUpload,
  Stop,
  Close,
  SmartToy,
  Edit,
  Videocam,
  DeleteOutline,
  SwapHoriz,
  Sync,
  Tune,
  FileDownload,
} from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { DateRangePicker } from 'react-date-range';
import { format, isSameDay, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import AudioPlayer from 'react-h5-audio-player';
import toWav from 'audiobuffer-to-wav';
import 'react-h5-audio-player/lib/styles.css';
import {
  getStoredToken,
  softDeleteFace,
  getFaceImage,
  restoreFace,
  updateFaceImage,
  verifyFaceQualityBase64,
  registerFaceBase64,
  fetchOrgDesignations,
  fetchOrgDepartments,
  getVendorPersons,
  uploadEmployeeVoice,
  fetchCompanies,
  getKivoBranches,
  updatePersonName,
  updatePersonDetails,
  updatePersonCameraAccess,
  listAIVoiceApiKeys,
  getDefaultAIVoiceKeySelection,
  saveAIVoiceApiKey,
  generateEmployeeAIVoice,
  updateHrmsEmployeeValidity,
  getActivityCameras,
} from '../utils/api';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useRef } from 'react';
import WebcamCapture from './employeeEnrollment/WebcamCapture';
import PageHeader from './PageHeader';
import ClearAllFiltersButton from './ClearAllFiltersButton';
import PeopleExportDialog from './PeopleExportDialog';
import {
  pageFilterAutocompleteSx,
  pageFilterFormControlSx,
  pageFiltersActionsSx,
  pageFiltersControlsSx,
  pageFiltersStackSx,
  pageFiltersToolbarSx,
} from '../utils/pageFilterStyles';
import SimilarityThresholdDialog from './SimilarityThresholdDialog';
import PersonProfileSwitchDialog from './PersonProfileSwitchDialog';
import { VendorNameHyperlink } from './VendorNameHyperlink';
import { formatDate } from '../utils/dateUtils';
import {
  sanitizeNameSearchInput,
  filterPersonNameOptions,
  resolvePersonOptionBySearchText,
  formatPersonNameOptionLabel,
} from '../utils/nameSearchInput';
import { baseURL } from '../config/api';
import { PersonSearch } from './PersonSearch';
import { isIntegerPersonId, buildPersonDetailsUrl } from '../utils/personDetailsNavigation';
import PersonSummaryCard from './people/PersonSummaryCard';
import EmployeeFacesDialog from './people/EmployeeFacesDialog';
import { useImagePreview } from './ImagePreviewDialog';
import { useAuth } from '../contexts/AuthContext';
import { PersonSummaryCardSkeleton } from './shimmer-ui/PersonSummaryCardSkeleton';

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const primary = '#40518A';

const revokePreviewUrl = (url) => {
  if (url && String(url).startsWith('blob:')) URL.revokeObjectURL(url);
};

const directionForFaceAngle = (angle) => {
  const value = String(angle || '').toLowerCase();
  if (value === 'left' || value === 'left_profile') return 'right';
  if (value === 'right' || value === 'right_profile') return 'left';
  if (value === 'custom' || value === 'custom_angle' || value === 'top') return 'top';
  return 'front';
};

const dataUrlToFile = (dataUrl, filename = 'capture.jpg') => {
  const [header, body] = String(dataUrl || '').split(',');
  const mime = header?.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(body || '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
};
const Vendors = () => {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const { openImagePreview } = useImagePreview();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const parseBoolParam = (value) => {
    const normalized = String(value || '').toLowerCase();
    return normalized === 'true' || normalized === '1';
  };
  const sanitizeQueryParam = (value) => sanitizeNameSearchInput(value || '');
  const currentUserDisplayName = useMemo(() => {
    const sessionUser = sessionStorage.getItem('user');
    const storedUser = sessionUser || localStorage.getItem('user');
    if (!storedUser) return '';
    try {
      const parsed = JSON.parse(storedUser);
      return (
        parsed.full_name ||
        parsed.username ||
        parsed.email ||
        parsed.name ||
        ''
      );
    } catch (err) {
      console.error('Failed to parse stored user for display name', err);
      return '';
    }
  }, []);
  const [persons, setPersons] = useState([]);
  const [personThumbnails, setPersonThumbnails] = useState({});
  const personThumbnailsRef = useRef({});
  const thumbnailRequestsRef = useRef(new Set());
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
    has_next: false,
    has_prev: false,
    total_pages: 0,
    current_page: 1,
  });
  const [loading, setLoading] = useState(false);
  const [directoryExportOpen, setDirectoryExportOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(parseBoolParam(searchParams.get('enrolled_deleted')));
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [faceDetailDialog, setFaceDetailDialog] = useState({ open: false, face: null });
  const [facesModalPerson, setFacesModalPerson] = useState(null);
  const [imageDialog, setImageDialog] = useState({ open: false, imageUrl: null, faceName: '' });
  const [editDialog, setEditDialog] = useState({ open: false, face: null });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [editCameraOn, setEditCameraOn] = useState(false);
  const [editCaptureNonce, setEditCaptureNonce] = useState(0);
  const [editQualityResult, setEditQualityResult] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [nameEditDialog, setNameEditDialog] = useState({
    open: false,
    person: null,
    name: '',
    personId: '',
    companyId: '',
    branchId: '',
    designation: '',
    department: '',
    saving: false
  });
  const [customTrainingImages, setCustomTrainingImages] = useState([]);
  const customImageInputRef = useRef(null);
  // Removed unused syncLoading state
  const [enrolledQuery, setEnrolledQuery] = useState(sanitizeQueryParam(searchParams.get('enrolled_query')));
  /** When set, list is filtered to this vendor (from name autocomplete). Omitted `personId` in loadFaces uses this. */
  const [enrolledSearchPersonId, setEnrolledSearchPersonId] = useState(
    (searchParams.get('person_id') || '').trim() || null
  );
  const [vendorPersonNames, setVendorPersonNames] = useState([]);
  const [playingVoice, setPlayingVoice] = useState(null); // Track currently playing voice: { employeeId, voiceType } or null

  const didInitialPersonsLoadRef = useRef(false);
  const prevShowDeletedRef = useRef(showDeleted);

  // Edit dialog companies and branches
  const [editDialogCompanies, setEditDialogCompanies] = useState([]);
  const [editDialogBranches, setEditDialogBranches] = useState([]);
  const [editDialogDesignations, setEditDialogDesignations] = useState([]);
  const [editDialogDepartments, setEditDialogDepartments] = useState([]);

  // Voice Upload Modal state
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [voiceModalEmployee, setVoiceModalEmployee] = useState(null); // { employeeId, employeeName, personId }
  const [checkinVoiceBlob, setCheckinVoiceBlob] = useState(null);
  const [checkoutVoiceBlob, setCheckoutVoiceBlob] = useState(null);
  const [isRecordingCheckin, setIsRecordingCheckin] = useState(false);
  const [isRecordingCheckout, setIsRecordingCheckout] = useState(false);
  const [voiceUploading, setVoiceUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  // Audio URLs for player
  const [checkinAudioUrl, setCheckinAudioUrl] = useState(null);
  const [checkoutAudioUrl, setCheckoutAudioUrl] = useState(null);
  const [aiVoiceDialogOpen, setAiVoiceDialogOpen] = useState(false);
  const [aiVoiceType, setAiVoiceType] = useState('checkin');
  const [aiVoiceApiKeyInput, setAiVoiceApiKeyInput] = useState('');
  const [aiVoiceSavedKeys, setAiVoiceSavedKeys] = useState([]);
  const [aiVoiceSelectedKeyId, setAiVoiceSelectedKeyId] = useState('');
  const [aiVoiceText, setAiVoiceText] = useState('');
  const [aiVoiceLoadingKeys, setAiVoiceLoadingKeys] = useState(false);
  const [aiVoiceGenerating, setAiVoiceGenerating] = useState(false);

  // Find Person Modal state
  const [findPersonModalOpen, setFindPersonModalOpen] = useState(false);
  const [findPersonModalPerson, setFindPersonModalPerson] = useState(null);
  const [selectedAngle, setSelectedAngle] = useState('');
  const [similarityThreshold, setSimilarityThreshold] = useState('0.3');
  /** 'audit' = In/Out (audit) logs; 'activity' = activity monitoring logs */
  const [findPersonLogType, setFindPersonLogType] = useState('audit');
  const [findPersonLoading, setFindPersonLoading] = useState(false);
  /** Object URLs for Find Person modal face thumbnails (revoked on close / reload). */
  const [findPersonFaceThumbUrls, setFindPersonFaceThumbUrls] = useState({});
  const findPersonFaceThumbUrlsRef = useRef({});
  const [profileSwitchDialog, setProfileSwitchDialog] = useState({ open: false, person: null });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Helpers for datetime-local values (local time, no timezone)
  const pad2 = (n) => String(n).padStart(2, '0');
  const loadFacesRequestIdRef = useRef(0);
  // Per-person expand state for grouped rows
  const [rowExpanded, setRowExpandedState] = useState({});
  const setRowExpanded = (personId) => {
    setRowExpandedState(prev => ({ ...prev, [personId]: !prev[personId] }));
  };
  const [enrolledManageMenu, setEnrolledManageMenu] = useState({ anchorEl: null, person: null });
  const closeEnrolledManageMenu = () => setEnrolledManageMenu({ anchorEl: null, person: null });
  const [enrolledVoiceMenu, setEnrolledVoiceMenu] = useState({ anchorEl: null, person: null });
  const closeEnrolledVoiceMenu = () => setEnrolledVoiceMenu({ anchorEl: null, person: null });
  const [validityDialog, setValidityDialog] = useState({
    open: false,
    person: null,
    validFrom: '',
    validTill: '',
    unlimited: true,
    saving: false,
  });
  const [restrictedCameraDialog, setRestrictedCameraDialog] = useState({
    open: false,
    person: null,
    selectedIds: [],
    saving: false,
  });
  const [similarityThresholdDialog, setSimilarityThresholdDialog] = useState({
    open: false,
    person: null,
  });
  const [activityCameras, setActivityCameras] = useState([]);
  const [activityCamerasLoading, setActivityCamerasLoading] = useState(false);
  /** Nested picker reuses `DateRangePickerContent` (same as sync-style range UI + times). */
  const [validityPickerOpen, setValidityPickerOpen] = useState(false);
  const [validityDateRange, setValidityDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: 'selection' },
  ]);
  const [validityStartTime, setValidityStartTime] = useState('00:00');
  const [validityEndTime, setValidityEndTime] = useState('23:59');

  const toDateInputValue = (raw) => {
    if (!raw) return '';
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return '';
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const combineLocalDateAndTime = (dateObj, timeHHmm) => {
    const d = new Date(dateObj);
    const parts = String(timeHHmm || '00:00').split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    d.setHours(h, m, 0, 0);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:00`;
  };
  const [faceImages, setFaceImages] = useState({});
  const openValidityDialog = (person) => {
    const vf = person?.valid_from;
    const vt = person?.valid_till;
    const fromExisting = toDateInputValue(vf);
    const tillExisting = toDateInputValue(vt);
    let startD = vf ? new Date(vf) : new Date();
    let endD = vt ? new Date(vt) : vf ? new Date(startD) : new Date();
    if (Number.isNaN(startD.getTime())) startD = new Date();
    if (Number.isNaN(endD.getTime())) endD = new Date(startD.getTime());
    const st = vf ? format(new Date(vf), 'HH:mm') : '00:00';
    const et = vt ? format(new Date(vt), 'HH:mm') : '23:59';
    setValidityDateRange([{ startDate: startD, endDate: endD, key: 'selection' }]);
    setValidityStartTime(st);
    setValidityEndTime(et);
    setValidityPickerOpen(false);
    setValidityDialog({
      open: true,
      person,
      validFrom: vf ? combineLocalDateAndTime(startD, st) : '',
      validTill: vt ? combineLocalDateAndTime(endD, et) : '',
      unlimited: !fromExisting && !tillExisting,
      saving: false,
    });
  };

  const closeValidityDialog = () => {
    setValidityPickerOpen(false);
    setValidityDialog({ open: false, person: null, validFrom: '', validTill: '', unlimited: true, saving: false });
  };

  const applyValidityRangePicker = () => {
    const sel = validityDateRange[0];
    if (!sel?.startDate || !sel?.endDate) {
      showSnackbar('Please select a date range', 'warning');
      return;
    }
    const fromStr = combineLocalDateAndTime(sel.startDate, validityStartTime);
    const tillStr = combineLocalDateAndTime(sel.endDate, validityEndTime);
    if (fromStr > tillStr) {
      showSnackbar('Start date/time cannot be after end date/time', 'error');
      return;
    }
    setValidityDialog((prev) => ({
      ...prev,
      validFrom: fromStr,
      validTill: tillStr,
      unlimited: false,
    }));
    setValidityPickerOpen(false);
  };


  // Reusable Date Range Picker Content Component
  const DateRangePickerContent = ({
    dateRange,
    onDateRangeChange,
    /** When false (default), caps selection at today (sync-style). When true, allows future end dates (validity). */
    disableMaxDate = false,
    showTimeFields = false,
    startTime = '00:00',
    endTime = '23:59',
    onStartTimeChange,
    onEndTimeChange,
  }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        width: showTimeFields ? 'fit-content' : undefined,
        maxWidth: showTimeFields ? '100%' : undefined,
      }}
    >
      {/* Quick Select Presets */}
      <Box sx={{
        minWidth: 140,
        flexShrink: 0,
        borderRight: isMobile ? 'none' : '1px solid',
        borderBottom: isMobile ? '1px solid' : 'none',
        borderColor: 'divider',
        p: 2,
        display: 'flex',
        flexDirection: isMobile ? 'row' : 'column',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: 0.5
      }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, width: isMobile ? '100%' : 'auto' }}>
          QUICK SELECT
        </Typography>
        {[
          { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
          { label: 'Yesterday', getValue: () => ({ startDate: subDays(new Date(), 1), endDate: subDays(new Date(), 1) }) },
          { label: 'Last 7 Days', getValue: () => ({ startDate: subDays(new Date(), 6), endDate: new Date() }) },
          { label: 'Last 30 Days', getValue: () => ({ startDate: subDays(new Date(), 29), endDate: new Date() }) },
          { label: 'This Month', getValue: () => ({ startDate: startOfMonth(new Date()), endDate: new Date() }) },
          { label: 'Last Month', getValue: () => ({ startDate: startOfMonth(subMonths(new Date(), 1)), endDate: endOfMonth(subMonths(new Date(), 1)) }) },
        ].map((preset) => (
          <Button
            key={preset.label}
            size="small"
            variant="text"
            onClick={() => {
              const { startDate, endDate } = preset.getValue();
              onDateRangeChange([{ startDate, endDate, key: 'selection' }]);
            }}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              px: 1.5,
              py: 0.75,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
                color: 'primary.main',
              },
            }}
          >
            {preset.label}
          </Button>
        ))}
      </Box>

      {/* Date Range Picker */}
      <Box sx={{
        flex: showTimeFields ? '0 1 auto' : 1,
        minWidth: 0,
        p: 2,
        '& .rdrCalendarWrapper': {
          fontSize: '0.85rem',
        },
        '& .rdrMonth': {
          width: { xs: '280px', sm: '300px' },
        },
        '& .rdrDefinedRangesWrapper': {
          display: 'none',
        },
      }}>
        <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Selected Range:</strong>{' '}
            {isSameDay(dateRange[0].startDate, dateRange[0].endDate)
              ? format(dateRange[0].startDate, 'MMM dd, yyyy')
              : `${format(dateRange[0].startDate, 'MMM dd, yyyy')} - ${format(dateRange[0].endDate, 'MMM dd, yyyy')}`}
          </Typography>
        </Box>
        <Box
          sx={{
            width: showTimeFields ? 'max-content' : undefined,
            maxWidth: showTimeFields ? '100%' : undefined,
          }}
        >
          <DateRangePicker
            ranges={dateRange}
            onChange={(ranges) => onDateRangeChange([ranges.selection])}
            moveRangeOnFirstSelection={false}
            months={isMobile ? 1 : 2}
            direction={isMobile ? 'vertical' : 'horizontal'}
            rangeColors={[primary]}
            staticRanges={[]}
            inputRanges={[]}
            {...(!disableMaxDate ? { maxDate: new Date() } : {})}
          />
          {showTimeFields && (
            <Box
              sx={{
                px: 0,
                pb: 2,
                pt: 1,
                display: 'flex',
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <TextField
                label="Start Time"
                type="time"
                size="small"
                value={startTime}
                onChange={(e) => onStartTimeChange?.(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                fullWidth
              />
              <TextField
                label="End Time"
                type="time"
                size="small"
                value={endTime}
                onChange={(e) => onEndTimeChange?.(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                fullWidth
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  // Format angle label: drop "_profile" suffix for left/right
  const formatAngleLabel = useCallback((angle, metadata = {}) => {
    if (metadata && metadata.custom_angle_label) return metadata.custom_angle_label;
    if (!angle) return '';
    const s = String(angle).toLowerCase();
    if (s === 'left_profile') return 'left';
    if (s === 'right_profile') return 'right';
    if (s === 'custom_angle') return 'custom';
    return s.replace('_', ' ');
  }, []);
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadActivityCameras = async () => {
    try {
      setActivityCamerasLoading(true);
      const res = await getActivityCameras();
      const rows = Array.isArray(res?.cameras) ? res.cameras : [];
      const mapped = rows
        .map((cam) => {
          const id = String(cam?.camera_id || cam?.id || '').trim();
          const label = String(cam?.camera_label || cam?.label || cam?.name || id || '').trim();
          const description = String(
            cam?.description ||
            cam?.camera_description ||
            cam?.camera_desc ||
            ''
          ).trim();
          return { id, label, description };
        })
        .filter((cam) => cam.id);
      setActivityCameras(mapped);
    } catch (e) {
      console.error('Failed to load activity cameras:', e);
      showSnackbar('Failed to load activity cameras', 'error');
    } finally {
      setActivityCamerasLoading(false);
    }
  };

  const openNameEditDialog = async (person) => {
    const editableDesignation = (
      person?.designation ??
      person?.designation_name ??
      person?.job_title ??
      person?.title ??
      person?.position ??
      ''
    );
    const editableDepartment = (
      person?.department ??
      person?.department_name ??
      person?.dept ??
      person?.dept_name ??
      person?.organization_unit ??
      person?.org_unit ??
      ''
    );
    setNameEditDialog({
      open: true,
      person,
      name: person?.name || '',
      personId: person?.person_id || '',
      companyId: person?.company_id || '',
      branchId: person?.branch_id || '',
      designation: typeof editableDesignation === 'string' ? editableDesignation : String(editableDesignation || ''),
      department: typeof editableDepartment === 'string' ? editableDepartment : String(editableDepartment || ''),
      saving: false,
    });

    // Load companies/branches/designation/department options for the dropdowns
    try {
      const companiesData = await fetchCompanies(100, 0, false);
      setEditDialogCompanies(companiesData?.companies || []);

      // If person has a company, load branches for that company
      if (person?.company_id) {
        const [branchesData, designationRes, departmentRes] = await Promise.all([
          getKivoBranches(person.company_id),
          fetchOrgDesignations({
            permissions,
            companyId: person.company_id,
            branchId: person?.branch_id || null,
            includeInactive: false,
            limit: 200,
          }),
          fetchOrgDepartments({
            permissions,
            companyId: person.company_id,
            branchId: person?.branch_id || null,
            includeInactive: false,
            limit: 200,
          }),
        ]);
        setEditDialogBranches(branchesData?.branches || []);
        setEditDialogDesignations(Array.isArray(designationRes?.designations) ? designationRes.designations : []);
        setEditDialogDepartments(Array.isArray(departmentRes?.departments) ? departmentRes.departments : []);
      } else {
        setEditDialogBranches([]);
        setEditDialogDesignations([]);
        setEditDialogDepartments([]);
      }
    } catch (error) {
      console.error('Error loading companies/branches:', error);
      setEditDialogDesignations([]);
      setEditDialogDepartments([]);
    }
  };

  const closeNameEditDialog = () => {
    setNameEditDialog({
      open: false,
      person: null,
      name: '',
      personId: '',
      companyId: '',
      branchId: '',
      designation: '',
      department: '',
      saving: false
    });
    setCustomTrainingImages([]);
    setEditDialogCompanies([]);
    setEditDialogBranches([]);
    setEditDialogDesignations([]);
    setEditDialogDepartments([]);
  };

  const handleCustomImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);

    // Add to the list of custom images with empty label
    setCustomTrainingImages(prev => [...prev, {
      file,
      previewUrl,
      id: Date.now(),
      label: '' // Initialize with empty label
    }]);

    // Reset input so same file can be selected again
    if (customImageInputRef.current) {
      customImageInputRef.current.value = '';
    }
  };

  const handleUpdateImageLabel = (imageId, newLabel) => {
    setCustomTrainingImages(prev =>
      prev.map(img => img.id === imageId ? { ...img, label: newLabel } : img)
    );
  };

  const handleRemoveCustomImage = (imageId) => {
    setCustomTrainingImages(prev => {
      const image = prev.find(img => img.id === imageId);
      if (image?.previewUrl) {
        URL.revokeObjectURL(image.previewUrl);
      }
      return prev.filter(img => img.id !== imageId);
    });
  };

  const handleOpenFaceGallery = async (person, face) => {
  if (!face?.face_id) return;

  try {
    const faces = Array.isArray(person?.faces)
      ? person.faces.filter((item) => !item.deleted_at)
      : [];

    const imageMap = { ...faceImages };

    const missingFaces = faces.filter(
      (item) => !imageMap[item.face_id]
    );

    const loadedImages = await Promise.all(
      missingFaces.map(async (item) => {
        try {
          const blob = await getFaceImage(item.face_id);

          return {
            faceId: item.face_id,
            imageUrl: URL.createObjectURL(blob),
          };
        } catch (error) {
          console.error(
            'Error loading face:',
            item.face_id,
            error
          );
          return null;
        }
      })
    );

    const newlyLoaded = {};

    loadedImages.forEach((result) => {
      if (!result) return;

      imageMap[result.faceId] = result.imageUrl;
      newlyLoaded[result.faceId] = result.imageUrl;
    });

    if (Object.keys(newlyLoaded).length > 0) {
      setFaceImages((prev) => ({
        ...prev,
        ...newlyLoaded,
      }));
    }

    const images = faces
      .map((item) => {
        const src = imageMap[item.face_id];
        if (!src) return null;

        const label =
          item.face_angle || item.name || 'Face';

        return {
          src,
          title: label,
          alt: `Face - ${label}`,
        };
      })
      .filter(Boolean);

    const clickedSrc = imageMap[face.face_id];

    if (!clickedSrc || images.length === 0) return;

    const index = images.findIndex(
      (item) => item.src === clickedSrc
    );

    const frontFace = faces.find(
      (item) => item.face_angle === 'front'
    );

    openImagePreview({
      src: clickedSrc,
      title: face.face_angle || face.name || 'Face Image',
      alt: `Face - ${face.face_angle || face.name || 'Face'}`,
      images,
      index: Math.max(0, index),
      employeeName: person?.name || 'Unknown person',
      employeeId:
        person?.employee_code || person?.person_id || '',
      employeeImage:
        (frontFace && imageMap[frontFace.face_id]) ||
        images[0]?.src ||
        '',
    });
  } catch (error) {
    console.error('Error opening face gallery:', error);
  }
};

  const handleEditDialogCompanyChange = async (companyId) => {
    setNameEditDialog(prev => ({
      ...prev,
      companyId,
      branchId: '' // Reset branch when company changes
    }));

    // Load branches for the selected company
    if (companyId) {
      try {
        const [branchesData, designationRes, departmentRes] = await Promise.all([
          getKivoBranches(companyId),
          fetchOrgDesignations({
            permissions,
            companyId,
            branchId: null,
            includeInactive: false,
            limit: 200,
          }),
          fetchOrgDepartments({
            permissions,
            companyId,
            branchId: null,
            includeInactive: false,
            limit: 200,
          }),
        ]);
        setEditDialogBranches(branchesData?.branches || []);
        setEditDialogDesignations(Array.isArray(designationRes?.designations) ? designationRes.designations : []);
        setEditDialogDepartments(Array.isArray(departmentRes?.departments) ? departmentRes.departments : []);
      } catch (error) {
        console.error('Error loading branches:', error);
        setEditDialogBranches([]);
        setEditDialogDesignations([]);
        setEditDialogDepartments([]);
      }
    } else {
      setEditDialogBranches([]);
      setEditDialogDesignations([]);
      setEditDialogDepartments([]);
    }
  };

  const handleEditDialogBranchChange = async (branchId) => {
    setNameEditDialog((prev) => ({ ...prev, branchId }));
    const companyId = String(nameEditDialog?.companyId || '').trim();
    if (!companyId) {
      setEditDialogDesignations([]);
      setEditDialogDepartments([]);
      return;
    }
    try {
      const [designationRes, departmentRes] = await Promise.all([
        fetchOrgDesignations({
          permissions,
          companyId,
          branchId: branchId || null,
          includeInactive: false,
          limit: 200,
        }),
        fetchOrgDepartments({
          permissions,
          companyId,
          branchId: branchId || null,
          includeInactive: false,
          limit: 200,
        }),
      ]);
      setEditDialogDesignations(Array.isArray(designationRes?.designations) ? designationRes.designations : []);
      setEditDialogDepartments(Array.isArray(departmentRes?.departments) ? departmentRes.departments : []);
    } catch (error) {
      console.error('Error loading designation/department options:', error);
      setEditDialogDesignations([]);
      setEditDialogDepartments([]);
    }
  };

  const editDesignationOptions = useMemo(() => {
    const list = Array.isArray(editDialogDesignations) ? editDialogDesignations : [];
    const normalized = Array.from(new Set(list.map((v) => String(v || '').trim()).filter(Boolean)));
    const selected = String(nameEditDialog?.designation || '').trim();
    if (selected && !normalized.includes(selected)) return [selected, ...normalized];
    return normalized;
  }, [editDialogDesignations, nameEditDialog?.designation]);

  const editDepartmentOptions = useMemo(() => {
    const list = Array.isArray(editDialogDepartments) ? editDialogDepartments : [];
    const normalized = Array.from(new Set(list.map((v) => String(v || '').trim()).filter(Boolean)));
    const selected = String(nameEditDialog?.department || '').trim();
    if (selected && !normalized.includes(selected)) return [selected, ...normalized];
    return normalized;
  }, [editDialogDepartments, nameEditDialog?.department]);

  const handleSavePersonName = async () => {
    const oldPersonId = nameEditDialog?.person?.person_id;
    const newName = String(nameEditDialog?.name || '').trim();
    const newPersonId = String(nameEditDialog?.personId || '').trim();
    const newCompanyId = String(nameEditDialog?.companyId || '').trim();
    const newBranchId = String(nameEditDialog?.branchId || '').trim();
    const newDesignation = String(nameEditDialog?.designation || '').trim();
    const newDepartment = String(nameEditDialog?.department || '').trim();
    const identityLocked = isIntegerPersonId(oldPersonId);
    const currentDesignation = String(
      nameEditDialog?.person?.designation ??
      nameEditDialog?.person?.designation_name ??
      nameEditDialog?.person?.job_title ??
      nameEditDialog?.person?.title ??
      nameEditDialog?.person?.position ??
      ''
    ).trim();
    const currentDepartment = String(
      nameEditDialog?.person?.department ??
      nameEditDialog?.person?.department_name ??
      nameEditDialog?.person?.dept ??
      nameEditDialog?.person?.dept_name ??
      nameEditDialog?.person?.organization_unit ??
      nameEditDialog?.person?.org_unit ??
      ''
    ).trim();

    if (!oldPersonId) {
      showSnackbar('Person ID is missing', 'error');
      return;
    }
    if (!newName) {
      showSnackbar('Name is required', 'error');
      return;
    }

    try {
      setNameEditDialog((prev) => ({ ...prev, saving: true }));

      // Update name
      await updatePersonName(oldPersonId, newName);

      // Update editable details
      let finalPersonId = oldPersonId;

      const hasLocalIdentityUpdates = !identityLocked && (newPersonId || newCompanyId || newBranchId);
      const hasDesignationChange = newDesignation !== currentDesignation;
      const hasDepartmentChange = newDepartment !== currentDepartment;

      if (hasLocalIdentityUpdates || hasDesignationChange || hasDepartmentChange) {
        try {
          const detailsResponse = await updatePersonDetails(
            oldPersonId,
            newPersonId || oldPersonId,
            newCompanyId || null,
            newBranchId || null,
            hasDesignationChange ? (newDesignation || null) : undefined,
            hasDepartmentChange ? (newDepartment || null) : undefined,
          );
          finalPersonId = detailsResponse?.new_person_id || newPersonId || oldPersonId;
        } catch (error) {
          console.error('Error updating person details:', error);
          showSnackbar('Name updated but failed to update person details', 'warning');
        }
      }

      const updatedAtIso = new Date().toISOString();
      const updatedByName = currentUserDisplayName || 'system';

      setPersons((prev) => prev.map((p) => (
        p.person_id === oldPersonId ? {
          ...p,
          name: newName,
          person_id: finalPersonId,
          company_id: newCompanyId || p.company_id,
          branch_id: newBranchId || p.branch_id,
          designation: hasDesignationChange ? (newDesignation || null) : p.designation,
          department: hasDepartmentChange ? (newDepartment || null) : p.department,
          updated_at: updatedAtIso,
          updated_by: updatedByName,
          updated_by_name: updatedByName,
        } : p
      )));

      // Upload custom training images if any are selected
      if (customTrainingImages.length > 0) {
        let successCount = 0;
        let failCount = 0;

        for (const image of customTrainingImages) {
          try {
            // Convert file to base64
            const dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(image.file);
            });

            // Upload the image with label
            const res = await registerFaceBase64(
              newName,
              finalPersonId,
              dataUrl,
              'jpeg',
              'custom',
              image.label.trim() || undefined // Use label if provided
            );

            if (res && res.success) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (error) {
            console.error('Upload error for image:', error);
            failCount++;
          }
        }

        // Clean up preview URLs
        customTrainingImages.forEach(img => {
          if (img.previewUrl) {
            URL.revokeObjectURL(img.previewUrl);
          }
        });

        // Show upload results
        if (successCount > 0) {
          showSnackbar(`Employee details updated and ${successCount} custom training image(s) added successfully`, 'success');
          loadFaces(pagination.limit, pagination.offset);
        } else if (failCount > 0) {
          showSnackbar(`Employee details updated, but failed to add ${failCount} image(s)`, 'warning');
        }
      } else {
        showSnackbar('Employee details updated successfully', 'success');
      }

      closeNameEditDialog();
    } catch (error) {
      showSnackbar(error.message || 'Failed to update employee details', 'error');
      setNameEditDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleSaveValidity = async () => {
    const employeeId = validityDialog?.person?.person_id;
    if (!employeeId) {
      showSnackbar('Employee ID is missing', 'error');
      return;
    }
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const validFromPayload = validityDialog.unlimited ? null : (validityDialog.validFrom || null);
    const validTillPayload = validityDialog.unlimited ? null : (validityDialog.validTill || null);
    // If not unlimited, at least one boundary should be set
    if (!validityDialog.unlimited && !validFromPayload && !validTillPayload) {
      showSnackbar('Please select Valid From or Valid Till', 'error');
      return;
    }
    // Range check
    if (validFromPayload && validTillPayload && validFromPayload > validTillPayload) {
      showSnackbar('Valid From cannot be after Valid Till', 'error');
      return;
    }
    try {
      setValidityDialog((prev) => ({ ...prev, saving: true }));

      await updateHrmsEmployeeValidity(employeeId, validFromPayload, validTillPayload, tz);

      setPersons((prev) =>
        prev.map((p) =>
          String(p.person_id) === String(employeeId)
            ? { ...p, valid_from: validFromPayload, valid_till: validTillPayload }
            : p
        )
      );

      showSnackbar(
        validityDialog.unlimited ? 'Employee validity set to Unlimited' : 'Employee validity updated',
        'success'
      );
      closeValidityDialog();
    } catch (error) {
      showSnackbar(error.message || 'Failed to update validity', 'error');
      setValidityDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const resolveUpdatedByName = useCallback((person) => {
    const rawValue =
      person?.updated_by_name ??
      person?.updated_by ??
      person?.last_updated_by ??
      person?.updatedBy ??
      person?.updated_by_user?.name ??
      person?.updated_by_user?.full_name ??
      person?.updated_by_user?.username;

    if (!rawValue) return '-';
    if (typeof rawValue === 'string') return rawValue.trim() || '-';
    if (typeof rawValue === 'object') {
      const nestedValue = rawValue?.name ?? rawValue?.full_name ?? rawValue?.username;
      if (!nestedValue) return '-';
      return String(nestedValue).trim() || '-';
    }
    return String(rawValue);
  }, []);

  const resolveUpdatedAt = useCallback((person) => {
    const rawValue =
      person?.updated_at ??
      person?.last_updated ??
      person?.updatedAt ??
      person?.updated_on ??
      person?.modified_at ??
      person?.modified_on;
    const formatted = formatDate(rawValue);
    return formatted && formatted !== '-' ? formatted : '-';
  }, []);

  const resolveCreatedByName = useCallback((person) => {
    const rawValue =
      person?.created_by_name ??
      person?.created_by ??
      person?.last_created_by ??
      person?.createdBy ??
      person?.created_by_user?.name ??
      person?.created_by_user?.full_name ??
      person?.created_by_user?.username;

    if (!rawValue) return '-';
    if (typeof rawValue === 'string') return rawValue.trim() || '-';
    if (typeof rawValue === 'object') {
      const nestedValue = rawValue?.name ?? rawValue?.full_name ?? rawValue?.username;
      if (!nestedValue) return '-';
      return String(nestedValue).trim() || '-';
    }
    return String(rawValue);
  }, []);

  const resolveCreatedAt = useCallback((person) => {
    const rawValue =
      person?.created_at ??
      person?.createdAt ??
      person?.created_on ??
      person?.inserted_at;
    const formatted = formatDate(rawValue);
    return formatted && formatted !== '-' ? formatted : '-';
  }, []);

  const resolveDesignation = useCallback((person) => {
    const rawValue =
      person?.designation ??
      person?.designation_name ??
      person?.job_title ??
      person?.title ??
      person?.position;

    if (!rawValue) return '-';
    if (typeof rawValue === 'string') return rawValue.trim() || '-';
    if (typeof rawValue === 'object') {
      const nestedValue =
        rawValue?.name ??
        rawValue?.title ??
        rawValue?.designation ??
        rawValue?.position;
      if (!nestedValue) return '-';
      return String(nestedValue).trim() || '-';
    }
    return String(rawValue);
  }, []);

  const resolveDepartment = useCallback((person) => {
    const rawValue =
      person?.department ??
      person?.department_name ??
      person?.dept ??
      person?.dept_name ??
      person?.organization_unit ??
      person?.org_unit;

    if (!rawValue) return '-';
    if (typeof rawValue === 'string') return rawValue.trim() || '-';
    if (typeof rawValue === 'object') {
      const nestedValue =
        rawValue?.name ??
        rawValue?.title ??
        rawValue?.department ??
        rawValue?.dept;
      if (!nestedValue) return '-';
      return String(nestedValue).trim() || '-';
    }
    return String(rawValue);
  }, []);

  const mainTableColumns = useMemo(() => {
    const safePersons = Array.isArray(persons) ? persons : [];

    const angleLabelForPerson = (person) => {
      if (!person || !Array.isArray(person.faces) || person.faces.length === 0) {
        return '-';
      }
      return person.faces
        .map((face) => formatAngleLabel(face.face_angle, face.metadata))
        .filter(Boolean)
        .join(', ');
    };

    const columnDefinitions = [
      {
        id: 'name',
        label: 'Name',
        accessor: (p) => p?.name || '-',
      },
      {
        id: 'createdBy',
        label: 'Created By',
        accessor: (p) => {
          const name = resolveCreatedByName(p);
          const time = resolveCreatedAt(p);
          if (name === '-' && time === '-') return '-';
          return `${name} ${time}`.trim();
        },
      },
      {
        id: 'updatedBy',
        label: 'Updated By',
        accessor: (p) => {
          const name = resolveUpdatedByName(p);
          const time = resolveUpdatedAt(p);
          if (name === '-' && time === '-') return '-';
          return `${name} ${time}`.trim();
        },
      },
      {
        id: 'angles',
        label: 'Angles',
        accessor: angleLabelForPerson,
      },
      {
        id: 'voice',
        label: 'Voice',
        accessor: (p) => {
          const hasCheckin = p?.checkin_voice_path && p.checkin_voice_path !== '';
          const hasCheckout = p?.checkout_voice_path && p.checkout_voice_path !== '';
          if (hasCheckin && hasCheckout) return 'Check-in, Check-out';
          if (hasCheckin) return 'Check-in';
          if (hasCheckout) return 'Check-out';
          return 'No voice';
        },
      },
      {
        id: 'details',
        label: 'Action',
        accessor: () => 'View Details',
      },
    ];

    const toLength = (value) => String(value ?? '').length;
    const maxCharsByColumn = {
      name: 18,
      createdBy: 26,
      updatedBy: 26,
      angles: 12,
      voice: 12,
      details: 13,
    };
    return columnDefinitions.map((column) => {
      const maxDataLength = safePersons.reduce(
        (max, person) => Math.max(max, toLength(column.accessor(person))),
        0
      );
      const maxChars = maxCharsByColumn[column.id] || 20;
      const widthInChars = Math.min(Math.max(column.label.length, maxDataLength, 8) + 4, maxChars);
      return {
        ...column,
        width: `${widthInChars}ch`,
      };
    });
  }, [
    persons,
    formatAngleLabel,
    resolveUpdatedByName,
    resolveUpdatedAt,
    resolveCreatedByName,
    resolveCreatedAt,
  ]);

  const columnWidthMap = useMemo(
    () =>
      mainTableColumns.reduce((acc, column) => {
        acc[column.id] = column.width;
        return acc;
      }, {}),
    [mainTableColumns]
  );

  const getTableCellWidthProps = (columnId, variant = 'body', width = null) => {
    const widthValue = width ? width : columnWidthMap[columnId] || 'auto';
    const compactTrailing = columnId === 'angles' || columnId === 'voice' || columnId === 'details';
    return {
      sx: {
        width: widthValue,
        minWidth: widthValue,
        maxWidth: isMobile ? 'unset' : widthValue,
        whiteSpace: variant === 'head' ? 'nowrap' : 'normal',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...(variant === 'body' ? { wordBreak: 'break-word' } : {}),
        ...(compactTrailing
          ? {
            pl: 0.75,
            pr: 0.75,
            overflow: 'visible',
          }
          : {}),
      },
    };
  };

  const enrolledVisibleColumns = useMemo(() => {
    if (isMobile) return ['name'];
    return mainTableColumns.map((column) => column.id);
  }, [isMobile, mainTableColumns]);

  const renderEnrolledAngles = (person) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title="View face angles">
          <Button
            size="small"
            variant="outlined"
            onClick={() => setFacesModalPerson(person)}
            sx={{
              textTransform: 'none',
              borderColor: primary,
              color: primary,
              '&:hover': { borderColor: primary, backgroundColor: 'rgba(64, 81, 138, 0.08)' },
            }}
          >
            Faces
          </Button>
        </Tooltip>
      </Box>
    );
  };

  const renderEnrolledVoice = (person) => {
    const menuOpen = Boolean(
      enrolledVoiceMenu.anchorEl &&
      enrolledVoiceMenu.person &&
      enrolledVoiceMenu.person.person_id === person.person_id
    );
    const hasIn = !!(person.checkin_voice_path && person.checkin_voice_path !== '');
    const hasOut = !!(person.checkout_voice_path && person.checkout_voice_path !== '');
    const playDisabled = !!playingVoice || showDeleted;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          endIcon={menuOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          onClick={(e) =>
            setEnrolledVoiceMenu((prev) =>
              prev.anchorEl && prev.person?.person_id === person.person_id
                ? { anchorEl: null, person: null }
                : { anchorEl: e.currentTarget, person }
            )
          }
          sx={{
            textTransform: 'none',
            borderColor: primary,
            color: primary,
            '&:hover': { borderColor: primary, backgroundColor: 'rgba(64, 81, 138, 0.08)' },
          }}
        >
          Voice
        </Button>
        <Menu
          anchorEl={enrolledVoiceMenu.anchorEl}
          open={menuOpen}
          onClose={closeEnrolledVoiceMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 200 } }}
        >
          <MenuItem
            disabled={!hasIn || playDisabled}
            onClick={() => {
              if (!hasIn || playDisabled) return;
              closeEnrolledVoiceMenu();
              playVoice(person.person_id, 'checkin');
            }}
          >
            <ListItemIcon>
              <VolumeUp fontSize="small" sx={{ color: hasIn && !playDisabled ? 'primary.main' : undefined }} />
            </ListItemIcon>
            <ListItemText primary="Play IN" />
          </MenuItem>
          <MenuItem
            disabled={!hasOut || playDisabled}
            onClick={() => {
              if (!hasOut || playDisabled) return;
              closeEnrolledVoiceMenu();
              playVoice(person.person_id, 'checkout');
            }}
          >
            <ListItemIcon>
              <VolumeUp
                fontSize="small"
                sx={{ color: hasOut && !playDisabled ? 'secondary.main' : undefined }}
              />
            </ListItemIcon>
            <ListItemText primary="Play OUT" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledVoiceMenu();
              if (hasIn && hasOut) {
                openVoiceModal(person.person_id, person.name, person.person_id, 'update');
              } else {
                openVoiceModal(person.person_id, person.name, person.person_id);
              }
            }}
          >
            <ListItemIcon>
              <Sync fontSize="small" sx={{ color: showDeleted ? undefined : 'primary.main' }} />
            </ListItemIcon>
            <ListItemText primary="Update voice" />
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  const renderEnrolledActions = (person) => {
    const menuOpen = Boolean(
      enrolledManageMenu.anchorEl &&
      enrolledManageMenu.person &&
      enrolledManageMenu.person.person_id === person.person_id
    );
    const logsDisabled =
      !person.faces || person.faces.length === 0 || person.faces.every((f) => f.deleted_at);

    const openRestrictedCameras = async () => {
      if (!activityCameras.length) {
        await loadActivityCameras();
      }
      const restrictedIds = Array.isArray(person?.restricted_camera_ids)
        ? person.restricted_camera_ids.map((id) => String(id || '').trim()).filter(Boolean)
        : [];
      setRestrictedCameraDialog({
        open: true,
        person,
        selectedIds: Array.from(new Set(restrictedIds)),
        saving: false,
      });
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          endIcon={menuOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          onClick={(e) =>
            setEnrolledManageMenu((prev) =>
              prev.anchorEl && prev.person?.person_id === person.person_id
                ? { anchorEl: null, person: null }
                : { anchorEl: e.currentTarget, person }
            )
          }
          sx={{
            textTransform: 'none',
            borderColor: primary,
            color: primary,
            '&:hover': { borderColor: primary, backgroundColor: 'rgba(64, 81, 138, 0.08)' },
          }}
        >
          Manage
        </Button>
        <Menu
          anchorEl={enrolledManageMenu.anchorEl}
          open={menuOpen}
          onClose={closeEnrolledManageMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 220 } }}
        >
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              openNameEditDialog(person);
            }}
          >
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit details" />
          </MenuItem>
          <MenuItem
            disabled={logsDisabled || showDeleted}
            onClick={() => {
              if (logsDisabled || showDeleted) return;
              closeEnrolledManageMenu();
              handleFindPerson(person);
            }}
          >
            <ListItemIcon>
              <Search fontSize="small" sx={{ color: logsDisabled || showDeleted ? undefined : primary }} />
            </ListItemIcon>
            <ListItemText primary="Find person in logs" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              setProfileSwitchDialog({ open: true, person });
            }}
          >
            <ListItemIcon>
              <SwapHoriz fontSize="small" sx={{ color: showDeleted ? undefined : primary }} />
            </ListItemIcon>
            <ListItemText primary="Switch profile" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              openValidityDialog(person);
            }}
          >
            <ListItemIcon>
              <CalendarMonth fontSize="small" sx={{ color: showDeleted ? undefined : '#5F7EBC' }} />
            </ListItemIcon>
            <ListItemText primary="Add validity tenure" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              openRestrictedCameras();
            }}
          >
            <ListItemIcon>
              <Videocam fontSize="small" sx={{ color: showDeleted ? undefined : primary }} />
            </ListItemIcon>
            <ListItemText primary="Restricted cameras" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeEnrolledManageMenu();
              setSimilarityThresholdDialog({ open: true, person });
            }}
          >
            <ListItemIcon>
              <Tune fontSize="small" sx={{ color: primary }} />
            </ListItemIcon>
            <ListItemText primary="Set similarity threshold" />
          </MenuItem>
          <Divider />
          {!showDeleted ? (
            <MenuItem
              onClick={() => {
                closeEnrolledManageMenu();
                handleDeleteAllFaces(person);
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <DeleteOutline fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText primary="Deactivate all faces" primaryTypographyProps={{ sx: { color: 'error.main' } }} />
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => {
                closeEnrolledManageMenu();
                handleRestoreAllFaces(person);
              }}
              sx={{ color: 'success.main' }}
            >
              <ListItemIcon>
                <Restore fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText primary="Activate all faces" primaryTypographyProps={{ sx: { color: 'success.main' } }} />
            </MenuItem>
          )}
        </Menu>
      </Box>
    );
  };

  const handleSimilarityThresholdSaved = useCallback((personId, threshold) => {
    setPersons((prev) =>
      prev.map((p) =>
        String(p?.person_id || '') === String(personId)
          ? { ...p, similarity_threshold: threshold }
          : p
      )
    );
  }, []);

  const renderEnrolledMobileDetails = (person) => (
    <Box sx={{ display: 'grid', gap: 1, py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="caption" color="text.secondary">Created By</Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2">{resolveCreatedByName(person)}</Typography>
          <Typography variant="caption" color="text.secondary">{resolveCreatedAt(person)}</Typography>
        </Box>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="caption" color="text.secondary">Updated By</Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2">{resolveUpdatedByName(person)}</Typography>
          <Typography variant="caption" color="text.secondary">{resolveUpdatedAt(person)}</Typography>
        </Box>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="caption" color="text.secondary">Angles</Typography>
        <Box sx={{ maxWidth: '65%' }}>{renderEnrolledAngles(person)}</Box>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="caption" color="text.secondary">Voice</Typography>
        <Box sx={{ maxWidth: '65%' }}>{renderEnrolledVoice(person)}</Box>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="caption" color="text.secondary">Actions</Typography>
        {renderEnrolledActions(person)}
      </Box>
    </Box>
  );

  const renderFaceRowActions = (faceData) => (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
      <Button
        title="View image"
        size="small"
        startIcon={<img src={"/assets/icons/view-image-icon.png"} alt="" style={{ width: 20, height: 20 }} />}
        onClick={() => handleViewFaceImage(faceData)}
      />
      {!showDeleted && (
        <Button
          title="Edit image"
          size="small"
          onClick={() => handleOpenEdit(faceData)}
          startIcon={<img src={"/assets/icons/edit-image-icon.png"} alt="" style={{ width: 20, height: 20 }} />}
          disableRipple
          sx={{ color: 'text.disabled' }}
        />
      )}
      {!showDeleted ? (
        <Button title="Deactivate" size="small" startIcon={<Delete />} style={{ color: '#5F7EBC' }} onClick={() => handleSoftDeleteFace(faceData)} />
      ) : (
        <Button title="Activate" size="small" startIcon={<Restore />} style={{ color: '#5F7EBC' }} onClick={() => handleRestoreFace(faceData)} />
      )}
    </Box>
  );

  const editSelectMenuProps = {
    PaperProps: {
      sx: {
        mt: 1,
        minWidth: 0,
        maxWidth: {
          xs: 120,    // extra small screens
          sm: 180,    // small screens
          md: 280,    // medium screens
          lg: 340,    // large screens
          xl: 420     // extra large screens
        },
        flex: { xs: 1, sm: .5 },
        maxHeight: { xs: 240, sm: 320 },
      },
    },
    MenuListProps: {
      dense: isMobile,
    },
  };

  const formatVendorPersonNameOption = useCallback((option) => {
    return formatPersonNameOptionLabel(option, { includeCode: true });
  }, []);

  const filterVendorPersonNameOptions = useCallback((options, state) => {
    return filterPersonNameOptions(options, state);
  }, []);

  const loadFaces = useCallback(async (
    limit = 10,
    offset = 0,
    query = enrolledQuery,
    personId = undefined,
    companyId = null,
    branchId = null,
    designation = null,
    department = null
  ) => {
    const resolvedPersonId = personId === undefined ? enrolledSearchPersonId : personId;
    const requestId = ++loadFacesRequestIdRef.current;
    setLoading(true);
    try {
      const fetchPage = async (targetOffset) => {
        const data = await getVendorPersons(
          limit,
          targetOffset,
          showDeleted,
          query && query.trim() ? query.trim() : null,
          resolvedPersonId,
          companyId,
          branchId,
          false,
          designation,
          department
        );
        console.log('Persons API response:', data);

        if (data.persons && data.pagination) {
          return {
            persons: data.persons,
            pagination: {
              ...data.pagination,
              limit: Number(data.pagination.limit) || limit,
              offset: Number(data.pagination.offset) || targetOffset,
              total: Number(data.pagination.total) || 0,
              total_pages: Number(data.pagination.total_pages) || 0,
              current_page: Number(data.pagination.current_page) || Math.floor(targetOffset / limit) + 1,
            },
          };
        }

        const personsArray = Array.isArray(data) ? data : [];
        return {
          persons: personsArray,
          pagination: {
            total: personsArray.length,
            limit,
            offset: targetOffset,
            has_next: false,
            has_prev: targetOffset > 0,
            total_pages: personsArray.length > 0 ? Math.ceil(personsArray.length / limit) : 0,
            current_page: Math.floor(targetOffset / limit) + 1,
          },
        };
      };

      let result = await fetchPage(offset);
      const total = Number(result.pagination?.total) || 0;
      const resultOffset = Number(result.pagination?.offset) || 0;

      if (result.persons.length === 0 && total > 0 && resultOffset >= total) {
        const safeOffset = Math.max(0, (Math.ceil(total / limit) - 1) * limit);
        result = await fetchPage(safeOffset);
      }

      if (requestId !== loadFacesRequestIdRef.current) {
        return;
      }

      const employeeCodes = result.persons
        .map(p => ({ name: p.name, person_id: p.person_id, employee_code: p.employee_code }))
        .filter(p => p.employee_code);
      if (employeeCodes.length > 0) {
        console.log('Processing persons with employee codes:', employeeCodes);
      }

      setPersons(result.persons);
      setPagination(result.pagination);
    } catch (error) {
      if (requestId !== loadFacesRequestIdRef.current) {
        return;
      }
      console.error('Error loading persons:', error);
      showSnackbar('Failed to load persons', 'error');
    } finally {
      if (requestId === loadFacesRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    showDeleted,
    enrolledQuery,
    enrolledSearchPersonId,
  ]);

  const loadPersonThumbnail = useCallback((person) => {
    const personId = person?.person_id;
    if (!personId || thumbnailRequestsRef.current.has(personId)) return;
    const faces = Array.isArray(person.faces) ? person.faces : [];
    const activeFaces = faces.filter(
      (face) => !face.deleted_at && face.face_id
    );

    // Prefer front face, then any active face.
    // If no active face exists (deactivated view), use any available face.
    const preferredFace =
      activeFaces.find(
        (face) => String(face.face_angle || "").trim().toLowerCase() === "front"
      ) ||
      activeFaces[0] ||
      faces.find((face) => face.face_id) ||
      null;
    const version = preferredFace ? `${preferredFace.face_id}:${preferredFace.last_updated || ''}` : 'none';
    const cached = personThumbnailsRef.current[personId];
    if (cached && cached.version === version) return;
    if (!preferredFace?.face_id) {
      personThumbnailsRef.current[personId] = { version, url: null };
      setPersonThumbnails((prev) => ({ ...prev, [personId]: null }));
      return;
    }
    thumbnailRequestsRef.current.add(personId);
    (async () => {
      try {
        const dataUrl = await blobToDataUrl(await getFaceImage(preferredFace.face_id));
        personThumbnailsRef.current[personId] = { version, url: dataUrl };
        setPersonThumbnails((prev) => ({ ...prev, [personId]: dataUrl }));
      } catch (error) {
        console.error('Failed to load vendor thumbnail:', error);
        personThumbnailsRef.current[personId] = { version, url: null };
        setPersonThumbnails((prev) => ({ ...prev, [personId]: null }));
      } finally {
        thumbnailRequestsRef.current.delete(personId);
      }
    })();
  }, []);

  const handleClearVendorSearch = useCallback(() => {
    setEnrolledSearchPersonId(null);
    setEnrolledQuery('');
    loadFaces(pagination.limit, 0, '', null);
  }, [loadFaces, pagination.limit]);

  // Show toast passed via navigation state (e.g., after registration)
  useEffect(() => {
    const toast = location?.state && location.state.toast;
    if (toast && toast.message) {
      showSnackbar(toast.message, toast.severity || 'success');
      // Clear state to avoid repeating on back/forward
      navigate('.', { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (event, page) => {
    const newOffset = (page - 1) * pagination.limit;
    loadFaces(pagination.limit, newOffset);
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    loadFaces(newLimit, 0);
  };

  const handleToggleDeleted = (event, newValue) => {
    // Only update if a value is selected (prevents deselecting both buttons)
    if (newValue !== null) {
      setEnrolledSearchPersonId(null);
      setShowDeleted(newValue === 'deleted');
      // Reset pagination when switching views
      setPagination(prev => ({ ...prev, offset: 0, current_page: 1 }));
    }
  };

  const handleViewFaceImage = async (face) => {
    try {
      const imageBlob = await getFaceImage(face.face_id);
      const imageUrl = URL.createObjectURL(imageBlob);
      setImageDialog({ open: true, imageUrl, faceName: face.name });
    } catch (error) {
      console.error('Error loading face image:', error);
      showSnackbar('Failed to load face image', 'error');
    }
  };

  const handleFindPerson = (person) => {
    if (!person || !person.faces || person.faces.length === 0) {
      showSnackbar('No faces found for this employee', 'error');
      return;
    }

    // Filter out deleted faces
    const availableFaces = person.faces.filter(f => !f.deleted_at);
    if (availableFaces.length === 0) {
      showSnackbar('No active faces found for this employee', 'error');
      return;
    }

    // Prefer front angle, otherwise use first available
    const frontFace = availableFaces.find(f => f.face_angle === 'front');
    const defaultFace = frontFace || availableFaces[0];

    setFindPersonModalPerson(person);
    setSelectedAngle(String(defaultFace.face_id));
    setSimilarityThreshold('0.3');
    setFindPersonLogType('audit');
    setFindPersonModalOpen(true);
  };

  const handleFindPersonModalClose = () => {
    setFindPersonModalOpen(false);
    setFindPersonModalPerson(null);
    setSelectedAngle('');
    setSimilarityThreshold('0.3');
    setFindPersonLogType('audit');
    setFindPersonLoading(false);
    Object.values(findPersonFaceThumbUrlsRef.current).forEach((u) => {
      if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
    });
    findPersonFaceThumbUrlsRef.current = {};
    setFindPersonFaceThumbUrls({});
  };

  useEffect(() => {
    if (!findPersonModalOpen || !findPersonModalPerson?.faces?.length) {
      Object.values(findPersonFaceThumbUrlsRef.current).forEach((u) => {
        if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
      });
      findPersonFaceThumbUrlsRef.current = {};
      setFindPersonFaceThumbUrls({});
      return undefined;
    }
    let cancelled = false;
    const faces = findPersonModalPerson.faces.filter((f) => !f.deleted_at);

    const loadThumbs = async () => {
      const next = {};
      await Promise.all(
        faces.map(async (f) => {
          const idKey = String(f.face_id);
          try {
            const blob = await getFaceImage(f.face_id);
            if (!cancelled) {
              next[idKey] = URL.createObjectURL(blob);
            }
          } catch {
            if (!cancelled) next[idKey] = '';
          }
        }),
      );
      if (cancelled) {
        Object.values(next).forEach((u) => {
          if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
        });
        return;
      }
      Object.values(findPersonFaceThumbUrlsRef.current).forEach((u) => {
        if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
      });
      findPersonFaceThumbUrlsRef.current = next;
      setFindPersonFaceThumbUrls({ ...next });
    };

    loadThumbs();
    return () => {
      cancelled = true;
    };
  }, [findPersonModalOpen, findPersonModalPerson]);

  const handleFindPersonSubmit = async () => {
    if (!findPersonModalPerson || !selectedAngle) {
      showSnackbar('Please select an angle', 'error');
      return;
    }

    const threshold = parseFloat(similarityThreshold);
    if (isNaN(threshold) || threshold < 0 || threshold > 1) {
      showSnackbar('Similarity threshold must be between 0 and 1', 'error');
      return;
    }

    try {
      setFindPersonLoading(true);

      // Find the selected face
      const selectedFace = findPersonModalPerson.faces.find(
        (f) => String(f.face_id) === String(selectedAngle),
      );
      if (!selectedFace) {
        showSnackbar('Selected face not found', 'error');
        return;
      }

      // Get the face image
      const imageBlob = await getFaceImage(selectedFace.face_id);

      // Convert blob to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result; // Full data URL: "data:image/jpeg;base64,..."

        const logsPath = findPersonLogType === 'activity' ? '/activity' : '/in-out';
        navigate(logsPath, {
          state: {
            findPersonImage: dataUrl,
            personId: findPersonModalPerson.person_id,
            personName: findPersonModalPerson.name,
            similarityThreshold: threshold,
          },
        });

        handleFindPersonModalClose();
      };
      reader.onerror = () => {
        showSnackbar('Failed to convert image to base64', 'error');
        setFindPersonLoading(false);
      };
      reader.readAsDataURL(imageBlob);
    } catch (error) {
      console.error('Error in find person:', error);
      showSnackbar(error.message || 'Failed to process image', 'error');
      setFindPersonLoading(false);
    }
  };

  const handleOpenEdit = (face) => {
    setEditDialog({ open: true, face });
    setSelectedFile(null);
    setEditCameraOn(false);
    setEditQualityResult(null);
    revokePreviewUrl(previewUrl);
    setPreviewUrl(null);
  };

  const handleCloseEdit = () => {
    setEditDialog({ open: false, face: null });
    setEditCameraOn(false);
    setEditQualityResult(null);
    revokePreviewUrl(previewUrl);
    setPreviewUrl(null);
  };

  const startEditCapture = () => {
    setEditQualityResult(null);
    setEditCaptureNonce((previous) => previous + 1);
    setEditCameraOn(true);
  };

  const applyVerifiedEditImage = async (imageBase64, file) => {
    setEditQualityResult({
      loading: true,
      success: false,
      message: 'Checking image quality...',
    });
    try {
      const result = await verifyFaceQualityBase64(imageBase64);
      setEditQualityResult({ ...result, loading: false });
      if (!result?.success) {
        setSelectedFile(null);
        showSnackbar(result?.message || 'Image rejected. Please capture a clearer photo.', 'error');
        setEditCaptureNonce((previous) => previous + 1);
        return;
      }
      setSelectedFile(file);
      revokePreviewUrl(previewUrl);
      setPreviewUrl(imageBase64);
      setEditCameraOn(false);
    } catch (error) {
      console.error('Image quality verification failed:', error);
      const errorResult = {
        loading: false,
        success: false,
        message: error.message || 'Failed to verify image quality',
        face_count: 0,
        pose_quality: 0,
        lighting_quality: 0,
        quality_score: 0,
        similarity: null,
        encoding_dimension: 0,
      };
      setEditQualityResult(errorResult);
      setSelectedFile(null);
      showSnackbar(errorResult.message, 'error');
    }
  };

  const handleEditAutoCaptured = (imageBase64) => {
    if (!imageBase64) return;
    try {
      const file = dataUrlToFile(imageBase64, 'capture.jpg');
      revokePreviewUrl(previewUrl);
      setPreviewUrl(imageBase64);
      setEditCameraOn(false);
      setSelectedFile(null);
      void applyVerifiedEditImage(imageBase64, file);
    } catch (error) {
      console.error('Failed to store captured image:', error);
      showSnackbar('Failed to capture photo', 'error');
      setEditCaptureNonce((previous) => previous + 1);
    }
  };

  const onFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setEditCameraOn(false);
    revokePreviewUrl(previewUrl);
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    if (!editDialog.open) {
      setSelectedFile(file);
      return;
    }
    setSelectedFile(null);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string') return;
      revokePreviewUrl(objectUrl);
      setPreviewUrl(dataUrl);
      void applyVerifiedEditImage(dataUrl, file);
    };
    reader.onerror = () => {
      showSnackbar('Unable to read selected image. Please try another file.', 'error');
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: false
      });

      streamRef.current = stream;
      setIsCameraOn(true);

      // Wait for video element to be rendered
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      console.error('Camera error:', err);
      showSnackbar('Unable to access camera', 'error');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) { }
      streamRef.current = null;
    }
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch (e) { }
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
  };

  const toggleCamera = async () => {
    if (streamRef.current) {
      stopCamera();
    } else {
      await startCamera();
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
      setSelectedFile(file);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      // stop camera after capture
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
      setIsCameraOn(false);
    }, 'image/jpeg', 0.92);
  };

  // eslint-disable-next-line no-unused-vars
  const retakeCapture = async () => { };

  const submitEdit = async () => {
    if (!editDialog.face || !selectedFile) {
      showSnackbar('Please select or capture an image', 'warning');
      return;
    }
    try {
      const updated = await updateFaceImage(editDialog.face.face_id, selectedFile);
      const faceId = editDialog.face.face_id;
      const refreshedAt = new Date().toISOString();
      showSnackbar('Image updated successfully', 'success');
      handleCloseEdit();
      setFacesModalPerson((current) => {
        if (!current?.faces?.some((face) => face.face_id === faceId)) return current;
        const personId = current.person_id;
        if (personId) delete personThumbnailsRef.current[personId];
        return {
          ...current,
          faces: current.faces.map((face) => (
            face.face_id === faceId
              ? {
                  ...face,
                  last_updated: refreshedAt,
                  pose_quality: updated?.pose_quality ?? face.pose_quality,
                  lighting_quality: updated?.lighting_quality ?? face.lighting_quality,
                }
              : face
          )),
        };
      });
      loadFaces(pagination.limit, pagination.offset);
    } catch (err) {
      console.error('Update failed:', err);
      showSnackbar(err.message || 'Failed to update image', 'error');
    }
  };

  const handleSoftDeleteFace = (face) => {
    setConfirmDialog({
      open: true,
      title: 'Soft Delete Face',
      message: `Are you sure you want to soft delete the face for ${face.name}? This can be restored later.`,
      onConfirm: () => performSoftDelete(face.face_id),
    });
  };

  const handleRestoreFace = (face) => {
    setConfirmDialog({
      open: true,
      title: 'Activate Face',
      message: `Are you sure you want to activate the face for ${face.name}? This will make it active again.`,
      onConfirm: () => performRestore(face.face_id),
    });
  };

  const performSoftDelete = async (faceId) => {
    try {
      const response = await softDeleteFace(faceId);

      if (response.ok) {
        // Update currently opened employee faces immediately.
        setFacesModalPerson((previousPerson) => {
          if (!previousPerson) return null;

          const updatedFaces = (previousPerson.faces || []).map((face) =>
            String(face.face_id) === String(faceId)
              ? {
                ...face,
                deleted_at: new Date().toISOString(),
              }
              : face
          );

          const activeFaces = updatedFaces.filter(
            (face) => !face.deleted_at
          );

          // Close dialog automatically if no active faces remain.
          if (!showDeleted && activeFaces.length === 0) {
            return null;
          }

          return {
            ...previousPerson,
            faces: updatedFaces,
          };
        });
        const currentPerson = facesModalPerson;

        if (currentPerson?.person_id) {
          delete personThumbnailsRef.current[currentPerson.person_id];

          setPersonThumbnails((previous) => ({
            ...previous,
            [currentPerson.person_id]: null,
          }));

          thumbnailRequestsRef.current.delete(currentPerson.person_id);
        }

        // Refresh employee list without affecting the dialog update.
        loadFaces(pagination.limit, pagination.offset);

        showSnackbar('Face soft deleted successfully', 'success');
      } else {
        showSnackbar('Failed to soft delete face', 'error');
      }
    } catch (error) {
      console.error('Error soft deleting face:', error);
      showSnackbar('Failed to soft delete face', 'error');
    }
  };

  const performRestore = async (faceId) => {
    try {
      const response = await restoreFace(faceId);

      if (response) {
        setFacesModalPerson((previousPerson) => {
          if (!previousPerson) return null;

          const updatedFaces = (previousPerson.faces || []).map((face) =>
            String(face.face_id) === String(faceId)
              ? { ...face, deleted_at: null }
              : face
          );

          return {
            ...previousPerson,
            faces: updatedFaces,
          };
        });

        showSnackbar('Face activated successfully', 'success');

        loadFaces(pagination.limit, pagination.offset);
      } else {
        showSnackbar('Failed to activate face', 'error');
      }
    } catch (error) {
      console.error('Error restoring face:', error);
      showSnackbar('Failed to activate face', 'error');
    }
  };

  const handleDeleteAllFaces = (person) => {
    const activeFaces = person.faces?.filter(face => !face.deleted_at) || [];
    if (activeFaces.length === 0) {
      showSnackbar('No active faces to delete', 'warning');
      return;
    }
    setConfirmDialog({
      open: true,
      title: 'Delete All Faces',
      message: `Are you sure you want to delete all ${activeFaces.length} face(s) for ${person.name}? This can be activated later.`,
      onConfirm: () => performDeleteAllFaces(activeFaces),
    });
  };

  const performDeleteAllFaces = async (faces) => {
    try {
      let successCount = 0;
      let failCount = 0;

      for (const face of faces) {
        try {
          const response = await softDeleteFace(face.face_id);
          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`Error deleting face ${face.face_id}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        showSnackbar(`Successfully deleted ${successCount} face(s)`, 'success');
        loadFaces(pagination.limit, pagination.offset);
      }
      if (failCount > 0) {
        showSnackbar(`Failed to delete ${failCount} face(s)`, 'error');
      }
    } catch (error) {
      console.error('Error deleting faces:', error);
      showSnackbar('Failed to delete faces', 'error');
    }
  };

  const handleRestoreAllFaces = (person) => {
    const deletedFaces = person.faces?.filter(face => face.deleted_at) || [];
    if (deletedFaces.length === 0) {
      showSnackbar('No deleted faces to activate', 'warning');
      return;
    }
    setConfirmDialog({
      open: true,
      title: 'Activate All Faces',
      message: `Are you sure you want to activate all ${deletedFaces.length} face(s) for ${person.name}? This will make them active again.`,
      onConfirm: () => performRestoreAllFaces(deletedFaces),
    });
  };

  const performRestoreAllFaces = async (faces) => {
    try {
      let successCount = 0;
      let failCount = 0;

      for (const face of faces) {
        try {
          const response = await restoreFace(face.face_id);
          if (response) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`Error activating face ${face.face_id}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        showSnackbar(`Successfully activated ${successCount} face(s)`, 'success');
        loadFaces(pagination.limit, pagination.offset);
      }
      if (failCount > 0) {
        showSnackbar(`Failed to activate ${failCount} face(s)`, 'error');
      }
    } catch (error) {
      console.error('Error activating faces:', error);
      showSnackbar('Failed to activate faces', 'error');
    }
  };

  useEffect(() => {
    // Check if user is authenticated
    const token = getStoredToken();
    if (!token) {
      navigate('/login');
      return;
    }

    const showDeletedChanged = prevShowDeletedRef.current !== showDeleted;
    prevShowDeletedRef.current = showDeleted;

    if (!didInitialPersonsLoadRef.current || showDeletedChanged) {
      didInitialPersonsLoadRef.current = true;
      loadFaces(
        pagination.limit,
        0,
        enrolledQuery,
        // `undefined` lets loadFaces fall back to the URL-hydrated
        // enrolledSearchPersonId so a deep-linked person_id filter is applied.
        undefined,
        null,
        null,
        null,
        null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, showDeleted]);


  const handleRegisterEmployee = () => {
    navigate('/vendors/register-vendor');
  };

  useEffect(() => {
    setSearchParams((prevParams) => {
      const params = new URLSearchParams(prevParams);
      params.delete('tab');
      if (enrolledQuery) params.set('enrolled_query', enrolledQuery); else params.delete('enrolled_query');
      if (enrolledSearchPersonId) params.set('person_id', enrolledSearchPersonId); else params.delete('person_id');
      if (showDeleted) params.set('enrolled_deleted', 'true'); else params.delete('enrolled_deleted');
      return params.toString() !== prevParams.toString() ? params : prevParams;
    }, { replace: true });
  }, [enrolledQuery, enrolledSearchPersonId, showDeleted, setSearchParams]);

  const handleRefresh = () => {
    loadFaces(pagination.limit, pagination.offset);
  };

  const getQualityColor = (quality) => {
    if (quality >= 0.9) return '#138636';
    if (quality >= 0.7) return '#FFBC11';
    return '#C91432';
  };

  const playVoice = async (employeeId, voiceType) => {
    // Prevent multiple simultaneous playback
    if (playingVoice) {
      showSnackbar('Please wait for the current voice to finish playing', 'warning');
      return;
    }

    try {
      console.log(`Playing ${voiceType} voice for employee ${employeeId}...`);

      // Set playing state immediately
      setPlayingVoice({ employeeId, voiceType });

      const token = getStoredToken();
      console.log('Token available:', !!token);

      if (!token) {
        setPlayingVoice(null);
        throw new Error('Authentication required - please login again');
      }

      // Fetch voice file from authenticated API endpoint using configured base URL
      const response = await fetch(`${baseURL}/api/v1/voice/${employeeId}/${voiceType}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'audio/wav'
        }
      });

      console.log('Response status:', response.status, 'Content-Type:', response.headers.get('content-type'));

      if (!response.ok) {
        setPlayingVoice(null);
        if (response.status === 401) {
          throw new Error('Session expired - please login again');
        }
        if (response.status === 404) {
          throw new Error('Voice file not found');
        }
        const errorText = await response.text();
        console.error('Voice fetch error:', response.status, errorText);
        throw new Error(`Failed to load voice: ${response.status}`);
      }

      // Check content type - should be audio/wav, not JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        setPlayingVoice(null);
        const errorJson = await response.json();
        throw new Error(errorJson.detail || 'Server returned error');
      }

      // Get the array buffer and create blob with explicit WAV MIME type
      const arrayBuffer = await response.arrayBuffer();

      if (arrayBuffer.byteLength < 100) {
        setPlayingVoice(null);
        throw new Error('Voice file is empty or corrupted');
      }

      const blob = new Blob([arrayBuffer], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(blob);

      console.log(' Audio blob created, size:', blob.size, 'bytes');

      const audio = new Audio();

      // Set up event handlers before setting src
      audio.onloadedmetadata = () => {
        console.log('Audio loaded! Duration:', audio.duration, 'seconds');
      };

      audio.oncanplaythrough = async () => {
        try {
          await audio.play();
          showSnackbar(`Playing ${voiceType} voice`, 'info');
        } catch (playError) {
          console.error('Play error:', playError);
          setPlayingVoice(null);
          showSnackbar(`Failed to play: ${playError.message}`, 'error');
        }
      };

      audio.onended = () => {
        console.log('Playback completed');
        URL.revokeObjectURL(audioUrl);
        setPlayingVoice(null); // Reset playing state when audio ends
      };

      audio.onerror = (err) => {
        console.error('Audio error:', err, audio.error);
        URL.revokeObjectURL(audioUrl);
        setPlayingVoice(null); // Reset playing state on error
        showSnackbar(`Failed to play voice: ${audio.error?.message || 'Unknown error'}`, 'error');
      };

      // Set source after event handlers
      audio.src = audioUrl;
      audio.load();

    } catch (error) {
      console.error('Error:', error);
      setPlayingVoice(null);
      showSnackbar(`Failed to play voice: ${error.message}`, 'error');
    }
  };

  // Voice Modal Functions
  const openVoiceModal = (employeeId, employeeName, personId = null, mode = 'add') => {
    setVoiceModalEmployee({ employeeId, employeeName, personId, mode });
    setCheckinVoiceBlob(null);
    setCheckoutVoiceBlob(null);
    setIsRecordingCheckin(false);
    setIsRecordingCheckout(false);
    setVoiceModalOpen(true);
  };

  const closeVoiceModal = () => {
    // Stop any active recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setVoiceModalOpen(false);
    setVoiceModalEmployee(null);
    setCheckinVoiceBlob(null);
    setCheckoutVoiceBlob(null);
    setIsRecordingCheckin(false);
    setIsRecordingCheckout(false);
  };

  const loadSavedAIVoiceKeys = async (autoSelectDefault = false) => {
    setAiVoiceLoadingKeys(true);
    try {
      const res = await listAIVoiceApiKeys();
      const keys = Array.isArray(res?.data) ? res.data : [];
      setAiVoiceSavedKeys(keys);
      if (autoSelectDefault) {
        const defaultKey = getDefaultAIVoiceKeySelection(keys);
        if (defaultKey) {
          setAiVoiceSelectedKeyId(defaultKey.credentialId);
          setAiVoiceApiKeyInput(defaultKey.hint);
        }
      }
      return keys;
    } catch (err) {
      console.error('Failed to load saved AI voice keys:', err);
      setAiVoiceSavedKeys([]);
      return [];
    } finally {
      setAiVoiceLoadingKeys(false);
    }
  };

  const openAIVoiceDialog = async (voiceType) => {
    if (!voiceModalEmployee) return;
    const employeeName = voiceModalEmployee?.employeeName || 'Employee';
    const defaultText = voiceType === 'checkout' ? `Goodbye ${employeeName}` : `Welcome ${employeeName}`;
    setAiVoiceType(voiceType);
    setAiVoiceText(defaultText);
    setAiVoiceApiKeyInput('');
    setAiVoiceSelectedKeyId('');
    setAiVoiceDialogOpen(true);
    await loadSavedAIVoiceKeys(true);
  };

  const closeAIVoiceDialog = () => {
    setAiVoiceDialogOpen(false);
    setAiVoiceApiKeyInput('');
    setAiVoiceSelectedKeyId('');
    setAiVoiceText('');
  };

  const base64ToWavBlob = (base64Audio) => {
    const byteChars = window.atob(base64Audio);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i += 1) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: 'audio/wav' });
  };

  const handleFileUpload = (event, voiceType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Strict .wav file validation by extension
    const fileName = file.name.toLowerCase();
    const isWavExtension = fileName.endsWith('.wav') || fileName.endsWith('.wave');

    if (!isWavExtension) {
      showSnackbar('Please upload a .wav extension audio file only', 'error');
      event.target.value = ''; // Reset input
      return;
    }

    if (voiceType === 'checkin') {
      setCheckinVoiceBlob(file);
    } else {
      setCheckoutVoiceBlob(file);
    }
    showSnackbar(`${voiceType === 'checkin' ? 'IN' : 'OUT'} voice file selected`, 'success');
  };

  const startRecording = async (voiceType) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Let browser choose supported mimeType (Safari doesn't support webm)
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        // Create blob from recorded chunks
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });

        try {
          const wavBlob = await convertToWav(audioBlob);
          if (voiceType === 'checkin') {
            setCheckinVoiceBlob(wavBlob);
            setIsRecordingCheckin(false);
          } else {
            setCheckoutVoiceBlob(wavBlob);
            setIsRecordingCheckout(false);
          }
          showSnackbar(`${voiceType === 'checkin' ? 'IN' : 'OUT'} voice recorded successfully`, 'success');
        } catch (err) {
          console.error('Error converting to WAV:', err);
          showSnackbar('Failed to process recording', 'error');
          if (voiceType === 'checkin') {
            setIsRecordingCheckin(false);
          } else {
            setIsRecordingCheckout(false);
          }
        }
      };

      // Start recording with timeslice for better performance
      mediaRecorder.start(100);
      if (voiceType === 'checkin') {
        setIsRecordingCheckin(true);
      } else {
        setIsRecordingCheckout(true);
      }
    } catch (err) {
      console.error('Error accessing microphone:', err);
      showSnackbar('Could not access microphone. Please allow microphone permission.', 'error');
    }
  };

  const stopRecording = (voiceType) => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  // Convert audio blob to wav blob using audiobuffer-to-wav library
  const convertToWav = async (audioBlob) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Use the optimized library for WAV conversion
    const wavBuffer = toWav(audioBuffer);
    audioContext.close(); // Clean up
    return new Blob([wavBuffer], { type: 'audio/wav' });
  };

  // Create audio URLs when blobs change
  React.useEffect(() => {
    if (checkinVoiceBlob) {
      const url = URL.createObjectURL(checkinVoiceBlob);
      setCheckinAudioUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setCheckinAudioUrl(null);
    }
  }, [checkinVoiceBlob]);

  React.useEffect(() => {
    if (checkoutVoiceBlob) {
      const url = URL.createObjectURL(checkoutVoiceBlob);
      setCheckoutAudioUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setCheckoutAudioUrl(null);
    }
  }, [checkoutVoiceBlob]);

  const saveVoices = async () => {
    if (!voiceModalEmployee) return;

    const { employeeId, personId } = voiceModalEmployee;
    const targetId = personId || employeeId;

    if (!checkinVoiceBlob && !checkoutVoiceBlob) {
      showSnackbar('Please upload or record at least one voice', 'warning');
      return;
    }

    setVoiceUploading(true);
    try {
      let successCount = 0;

      if (checkinVoiceBlob) {
        await uploadEmployeeVoice(targetId, 'checkin', checkinVoiceBlob);
        successCount++;
      }

      if (checkoutVoiceBlob) {
        await uploadEmployeeVoice(targetId, 'checkout', checkoutVoiceBlob);
        successCount++;
      }

      showSnackbar(`${successCount} voice(s) uploaded successfully`, 'success');
      closeVoiceModal();

      loadFaces(pagination.limit, pagination.offset, enrolledQuery);
    } catch (err) {
      console.error('Error uploading voice:', err);
      showSnackbar(`Failed to upload voice: ${err.message}`, 'error');
    } finally {
      setVoiceUploading(false);
    }
  };

  const handleGenerateAIVoice = async () => {
    if (!voiceModalEmployee) return;
    const { employeeId, personId } = voiceModalEmployee;
    const targetId = personId || employeeId;
    const trimmedKey = aiVoiceApiKeyInput.trim();
    const trimmedText = aiVoiceText.trim();

    if (!aiVoiceSelectedKeyId && !trimmedKey) {
      showSnackbar('Please select a saved key or enter a new API key', 'warning');
      return;
    }

    setAiVoiceGenerating(true);
    try {
      let payload = {};
      if (aiVoiceSelectedKeyId) {
        payload = {
          credential_id: aiVoiceSelectedKeyId,
          provider: 'cartesia',
          credential_type: 'voice_generation',
          text: trimmedText,
        };
      } else {
        const saveRes = await saveAIVoiceApiKey(trimmedKey);
        const savedCredentialId = saveRes?.data?.credential_id;
        if (savedCredentialId) {
          payload = {
            credential_id: savedCredentialId,
            provider: 'cartesia',
            credential_type: 'voice_generation',
            text: trimmedText,
          };
        } else {
          payload = {
            api_key: trimmedKey,
            provider: 'cartesia',
            credential_type: 'voice_generation',
            text: trimmedText,
          };
        }
      }

      payload.save_to_db = false;
      const res = await generateEmployeeAIVoice(targetId, aiVoiceType, payload);
      const audioBase64 = res?.audio_base64;
      if (!audioBase64) {
        throw new Error('Generated voice preview not received');
      }
      const wavBlob = base64ToWavBlob(audioBase64);
      if (aiVoiceType === 'checkin') {
        setCheckinVoiceBlob(wavBlob);
      } else {
        setCheckoutVoiceBlob(wavBlob);
      }
      showSnackbar(`${aiVoiceType === 'checkin' ? 'IN' : 'OUT'} voice generated. Click Save Voices to persist.`, 'success');
      closeAIVoiceDialog();

      loadFaces(pagination.limit, pagination.offset, enrolledQuery);
    } catch (err) {
      console.error('AI voice generation failed:', err);
      showSnackbar(`Failed to generate AI voice: ${err.message}`, 'error');
    } finally {
      setAiVoiceGenerating(false);
    }
  };

  const hasActiveFilters = useMemo(() => (
    !!(enrolledQuery?.trim()) || !!enrolledSearchPersonId || showDeleted
  ), [enrolledQuery, enrolledSearchPersonId, showDeleted]);

  const handleClearAllFilters = useCallback(() => {
    setEnrolledSearchPersonId(null);
    setEnrolledQuery('');
    setShowDeleted(false);
    loadFaces(pagination.limit, 0, '', null);
  }, [loadFaces, pagination.limit]);

  // Keep the PersonSearch suggestion query in sync with the vendor list filters.
  // Vendors have no company/branch/designation/department filters, so only the
  // vendor flag and deleted state are sent.
  const personSearchQueryParams = useMemo(() => ({
    offset: 0,
    include_deleted: showDeleted === true ? 'true' : 'false',
    is_vendor: 'true',
  }), [
    showDeleted,
  ]);

  return (
    <Box>
      <Container maxWidth="lg">
        <PageHeader
          title="Vendor Management"
          subtitle="Manage vendor face records and information"
        />

        {/* Filters Section - Above Tabs */}
        <Box sx={pageFiltersToolbarSx}>
          <Stack {...pageFiltersStackSx}>
            <Box sx={pageFiltersControlsSx}>
              <Tooltip
                title={enrolledQuery || ''}
                arrow
                placement="bottom"
                PopperProps={{ disablePortal: true }}
                disableHoverListener={!enrolledQuery}
              >
                <PersonSearch
                  queryParams={personSearchQueryParams}
                  onSelect={(emp) => {
                    if (!emp || !emp.person_id) return;
                    setEnrolledSearchPersonId(emp.person_id);
                    loadFaces(pagination.limit, 0, undefined, emp.person_id);
                  }}
                  onQueryChange={() => {
                    // Typing/clearing the name field drops the selected person_id
                    // filter so results fall back to a plain name search.
                    if (enrolledSearchPersonId) {
                      loadFaces(pagination.limit, 0, undefined, null);
                    }
                    setEnrolledSearchPersonId(null);
                  }}
                />
              </Tooltip>

              <ClearAllFiltersButton
                onClear={handleClearAllFilters}
                disabled={!hasActiveFilters}
              />
            </Box>

            <Box sx={pageFiltersActionsSx}>
              <>
                <ToggleButtonGroup
                  value={showDeleted ? 'deleted' : 'active'}
                  exclusive
                  onChange={handleToggleDeleted}
                  size="small"
                  sx={{
                    height: 36,
                    '& .MuiToggleButton-root': {
                      minHeight: 36,
                      paddingTop: 0,
                      paddingBottom: 0,
                      textTransform: 'none',
                      fontSize: '0.875rem'
                    },
                  }}
                >
                  <ToggleButton value="active">
                    <Visibility sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                    Active
                  </ToggleButton>
                  <ToggleButton value="deleted">
                    <Delete sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                    Deactive
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={loading}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    height: 36,
                    minWidth: 'auto',
                    px: 1.5,
                    fontSize: '0.875rem'
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={() => setDirectoryExportOpen(true)}
                  disabled={loading}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    height: 36,
                    minWidth: 'auto',
                    px: 1.5,
                    fontSize: '0.875rem'
                  }}
                >
                  Export
                </Button>
              </>

              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={handleRegisterEmployee}
                size="small"
                sx={{
                  textTransform: 'none',
                  height: 36,
                  minWidth: 'auto',
                  px: 1.5,
                  fontSize: '0.875rem',
                  ml: { xs: 0, md: 'auto' }
                }}
              >
                Register New Vendor
              </Button>
            </Box>
          </Stack>
        </Box>

        <Box>
          {loading ? (
            <Box sx={{ display: 'none', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ display: 'none', width: '100%', overflowX: { xs: 'hidden', sm: 'auto' }, WebkitOverflowScrolling: 'touch' }}>
              <Table
                size="medium"
                sx={{
                  minWidth: { xs: 0, sm: 720 },
                  tableLayout: isMobile ? 'auto' : 'fixed',
                }}
              >
                {!isMobile && (
                  <TableHead>
                    <TableRow>
                      {mainTableColumns
                        .filter((column) => enrolledVisibleColumns.includes(column.id))
                        .map((column) => (
                          <TableCell key={column.id} {...getTableCellWidthProps(column.id, 'head')}>
                            {column.label}
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {persons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(enrolledVisibleColumns.length, 1)} align="center">
                        No {showDeleted ? 'deleted' : 'active'} persons found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    persons.map(person => (
                      <React.Fragment key={person.person_id}>
                        <TableRow
                          sx={{
                            opacity: showDeleted ? 0.7 : 1,
                            backgroundColor: showDeleted ? 'rgba(255, 0, 0, 0.02)' : 'inherit',
                          }}
                        >
                          {enrolledVisibleColumns.includes('name') && (
                            <TableCell {...getTableCellWidthProps('name')}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <VendorNameHyperlink
                                  text={person.name || 'Unknown vendor'}
                                  vendors={[{ name: person.name, person_id: person.person_id }]}
                                  style={{ display: 'inline' }}
                                />
                              </Box>
                            </TableCell>
                          )}
                          {/* <TableCell {...getTableCellWidthProps('personId')}>
                                <Chip
                                  label={person.person_id}
                                  style={{ borderRadius: '6px', border: '1px solid', borderColor: 'black', backgroundColor: 'white' }}
                                  size="small"
                                />
                              </TableCell> */}
                          {enrolledVisibleColumns.includes('createdBy') && (
                            <TableCell {...getTableCellWidthProps('createdBy')}>
                              {(() => {
                                const name = resolveCreatedByName(person);
                                const time = resolveCreatedAt(person);
                                if (name === '-' && time === '-') {
                                  return <span style={{ color: '#999' }}>—</span>;
                                }
                                return (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                      {name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                      {time}
                                    </Typography>
                                  </Box>
                                );
                              })()}
                            </TableCell>
                          )}
                          {enrolledVisibleColumns.includes('employeeCode') && (
                            <TableCell {...getTableCellWidthProps('employeeCode')}>
                              {person.employee_code ? (
                                <Tooltip title={person.employee_code} arrow>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {person.employee_code}
                                  </Box>
                                  {/* <Chip
                                        label={person.employee_code}
                                        style={{ borderRadius: '6px', border: '1px solid', borderColor: '#1976d2', backgroundColor: 'white', color: '#1976d2' }}
                                        size="small"
                                      /> */}
                                </Tooltip>
                              ) : (
                                <span style={{ color: '#999' }}>—</span>
                              )}
                            </TableCell>
                          )}
                          {enrolledVisibleColumns.includes('designation') && (
                            <TableCell {...getTableCellWidthProps('designation')}>
                              {(() => {
                                const designation = resolveDesignation(person);
                                if (designation === '-') {
                                  return <span style={{ color: '#999' }}>—</span>;
                                }
                                return (
                                  <Tooltip title={designation} arrow>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      {designation}
                                    </Box>
                                  </Tooltip>
                                );
                              })()}
                            </TableCell>
                          )}
                          {enrolledVisibleColumns.includes('department') && (
                            <TableCell {...getTableCellWidthProps('department')}>
                              {(() => {
                                const department = resolveDepartment(person);
                                if (department === '-') {
                                  return <span style={{ color: '#999' }}>—</span>;
                                }
                                return (
                                  <Tooltip title={department} arrow>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      {department}
                                    </Box>
                                  </Tooltip>
                                );
                              })()}
                            </TableCell>
                          )}
                          {enrolledVisibleColumns.includes('updatedBy') && (
                            <TableCell {...getTableCellWidthProps('updatedBy')}>
                              {(() => {
                                const name = resolveUpdatedByName(person);
                                const time = resolveUpdatedAt(person);
                                if (name === '-' && time === '-') {
                                  return <span style={{ color: '#999' }}>—</span>;
                                }
                                return (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                      {name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                      {time}
                                    </Typography>
                                  </Box>
                                );
                              })()}
                            </TableCell>
                          )}
                          {enrolledVisibleColumns.includes('angles') && (
                            <TableCell {...getTableCellWidthProps('angles')}>
                              {renderEnrolledAngles(person)}
                            </TableCell>
                          )}
                          {enrolledVisibleColumns.includes('voice') && (
                            <TableCell {...getTableCellWidthProps('voice')}>
                              {renderEnrolledVoice(person)}
                            </TableCell>
                          )}
                          {/* <TableCell {...getTableCellWidthProps('status')}>
                                {(() => {
                                  const allDeleted = person.faces.every(x => !!x.deleted_at);
                                  const anyDeleted = person.faces.some(x => !!x.deleted_at);
                                  const label = allDeleted ? 'Deleted' : (anyDeleted ? 'Partial' : 'Active');
                                  const color = allDeleted ? 'error' : (anyDeleted ? 'warning' : 'success');
                                  return (
                                    <Chip label={label} color={color} size="small" sx={{ borderRadius: '6px', fontWeight: 'bold' }} />
                                  );
                                })()}
                              </TableCell> */}
                          {enrolledVisibleColumns.includes('details') && (
                            <TableCell {...getTableCellWidthProps('details')}>
                              {renderEnrolledActions(person)}
                            </TableCell>
                          )}
                        </TableRow>
                        {isMobile && (
                          <TableRow>
                            <TableCell colSpan={Math.max(enrolledVisibleColumns.length, 1)} sx={{ pt: 0, pb: 1.5 }}>
                              {renderEnrolledMobileDetails(person)}
                            </TableCell>
                          </TableRow>
                        )}
                        {!!rowExpanded[person.person_id] && (
                          <TableRow>
                            <TableCell
                              colSpan={Math.max(enrolledVisibleColumns.length, 1)}
                              sx={{ p: 0, width: '100%', display: 'table-cell', verticalAlign: 'top', borderBottom: 'none' }}
                            >
                              <Collapse in timeout={250} unmountOnExit>
                                <Box sx={{ backgroundColor: 'rgba(0,0,0,0.02)', px: 2, py: 1, width: '100%', boxSizing: 'border-box' }}>
                                  {isMobile ? (
                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                      {person.faces.map((af) => {
                                        const faceData = {
                                          face_id: af.face_id,
                                          face_angle: af.face_angle,
                                          pose_quality: af.pose_quality,
                                          lighting_quality: af.lighting_quality,
                                          created_at: af.created_at,
                                          last_updated: af.last_updated,
                                          deleted_at: af.deleted_at,
                                          metadata: af.metadata || {},
                                          name: person.name,
                                          person_id: person.person_id
                                        };
                                        return (
                                          <Paper key={af.face_id} variant="outlined" sx={{ p: 1.25 }}>
                                            <Box sx={{ display: 'grid', gap: 1 }}>
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Angle</Typography>
                                                <Chip label={formatAngleLabel(faceData.face_angle, faceData.metadata)} size="small" />
                                              </Box>
                                              <Divider />
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Pose</Typography>
                                                <Chip label={`${(faceData.pose_quality * 100).toFixed(1)}%`} size="small" sx={{ borderRadius: '6px', border: '1px solid', borderColor: getQualityColor(faceData.pose_quality), backgroundColor: 'white' }} />
                                              </Box>
                                              <Divider />
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Lighting</Typography>
                                                <Chip label={`${(faceData.lighting_quality * 100).toFixed(1)}%`} size="small" sx={{ borderRadius: '6px', border: '1px solid', borderColor: getQualityColor(faceData.lighting_quality), backgroundColor: 'white' }} />
                                              </Box>
                                              <Divider />
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Created</Typography>
                                                <Typography variant="body2" sx={{ textAlign: 'right' }}>{formatDate(faceData.created_at)}</Typography>
                                              </Box>
                                              <Divider />
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Updated</Typography>
                                                <Typography variant="body2" sx={{ textAlign: 'right' }}>{formatDate(faceData.last_updated)}</Typography>
                                              </Box>
                                              {showDeleted && (
                                                <>
                                                  <Divider />
                                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                                                    <Typography variant="body2" color="text.secondary">Deleted</Typography>
                                                    <Chip label={formatDate(faceData.deleted_at)} color="error" size="small" variant="outlined" />
                                                  </Box>
                                                </>
                                              )}
                                              <Divider />
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Actions</Typography>
                                                <Box sx={{ maxWidth: '70%' }}>{renderFaceRowActions(faceData)}</Box>
                                              </Box>
                                            </Box>
                                          </Paper>
                                        );
                                      })}
                                    </Box>
                                  ) : (
                                    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                                      <Table size="small" sx={{ m: 0, width: '100%', minWidth: showDeleted ? 860 : 760, tableLayout: 'fixed' }}>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ width: '14%' }}>Angle</TableCell>
                                            <TableCell sx={{ width: '12%' }}>Pose</TableCell>
                                            <TableCell sx={{ width: '12%' }}>Lighting</TableCell>
                                            <TableCell sx={{ width: '18%' }}>Created</TableCell>
                                            <TableCell sx={{ width: '18%' }}>Updated</TableCell>
                                            {showDeleted && <TableCell sx={{ width: '14%' }}>Deleted</TableCell>}
                                            <TableCell sx={{ width: showDeleted ? '12%' : '26%' }}>Actions</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {person.faces.map((af) => {
                                            const faceData = {
                                              face_id: af.face_id,
                                              face_angle: af.face_angle,
                                              pose_quality: af.pose_quality,
                                              lighting_quality: af.lighting_quality,
                                              created_at: af.created_at,
                                              last_updated: af.last_updated,
                                              deleted_at: af.deleted_at,
                                              metadata: af.metadata || {},
                                              name: person.name,
                                              person_id: person.person_id
                                            };
                                            return (
                                              <TableRow key={af.face_id}>
                                                <TableCell>
                                                  <Chip label={formatAngleLabel(faceData.face_angle, faceData.metadata)} size="small" />
                                                </TableCell>
                                                <TableCell>
                                                  <Chip label={`${(faceData.pose_quality * 100).toFixed(1)}%`} size="small" sx={{ borderRadius: '6px', border: '1px solid', borderColor: getQualityColor(faceData.pose_quality), backgroundColor: 'white' }} />
                                                </TableCell>
                                                <TableCell>
                                                  <Chip label={`${(faceData.lighting_quality * 100).toFixed(1)}%`} size="small" sx={{ borderRadius: '6px', border: '1px solid', borderColor: getQualityColor(faceData.lighting_quality), backgroundColor: 'white' }} />
                                                </TableCell>
                                                <TableCell>{formatDate(faceData.created_at)}</TableCell>
                                                <TableCell>{formatDate(faceData.last_updated)}</TableCell>
                                                {showDeleted && (
                                                  <TableCell>
                                                    <Chip label={formatDate(faceData.deleted_at)} color="error" size="small" variant="outlined" />
                                                  </TableCell>
                                                )}
                                                <TableCell>{renderFaceRowActions(faceData)}</TableCell>
                                              </TableRow>
                                            );
                                          })}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {loading ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' }, gap: 2 }}>
              {Array.from({ length: Math.min(pagination.limit || 6, 8) }).map((_, index) => <PersonSummaryCardSkeleton key={`vendor-skeleton-${index}`} />)}
            </Box>
          ) : persons.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: '#fafafa' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>No {showDeleted ? 'deleted' : 'active'} vendors found.</Typography>
              <Typography variant="body2" color="text.secondary">Registered vendors will appear here.</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' }, gap: 2, alignItems: 'stretch' }}>
              {persons.map((person) => {
                const faces = Array.isArray(person.faces) ? person.faces : [];
                const allDeleted = faces.length > 0 && faces.every((face) => !!face.deleted_at);
                const anyDeleted = faces.some((face) => !!face.deleted_at);
                const updatedBy = resolveUpdatedByName(person);
                const updatedAt = resolveUpdatedAt(person);
                return (
                  <PersonSummaryCard
                    key={person.person_id}
                    name={person.name || 'Unknown vendor'}
                    code={person.employee_code || ''}
                    imageSrc={personThumbnails[person.person_id] || ''}
                    category="Vendor"
                    statusLabel={allDeleted ? 'Inactive' : anyDeleted ? 'Partial' : 'Active'}
                    statusTone={allDeleted ? 'deactive' : anyDeleted ? 'partial' : 'active'}
                    muted={showDeleted || allDeleted}
                    details={[
                      { label: 'Company', value: person.company_name || '-' },
                      { label: 'Designation', value: resolveDesignation(person) },
                      { label: 'Department', value: resolveDepartment(person) },
                      { label: 'Updated by', value: updatedBy },
                      { label: 'Last Update at', value: updatedAt }
                    ]}
                    onClick={() => {
                      const url = buildPersonDetailsUrl(person.person_id, { detailKind: 'vendor' });
                      if (url) navigate(url);
                    }}
                    onRequestImage={() => loadPersonThumbnail(person)}
                    onImageClick={() => {
                      const image = personThumbnails[person.person_id];
                      if (!image) return;
                      openImagePreview({
                        src: image,
                        title: person.name || 'Profile image',
                        alt: person.name || 'Profile image',
                        employeeName: person.name || 'Unknown vendor',
                        employeeId: person.employee_code || person.person_id || '',
                        employeeImage: image,
                        hideTitle: true,
                      });
                    }}
                    actions={<Stack direction="row" spacing={0.75} flexWrap="no-wrap" useFlexGap
                      sx={{
                        width: '100%',
                        '& > .MuiBox-root': {
                          flex: '1 1 auto',
                          minWidth: 0,
                        },
                        '& .MuiButton-root': {
                          width: '100%',
                          minWidth: 0,
                          px: 0.75,
                          whiteSpace: 'nowrap',
                        },
                        '& .MuiButton-endIcon': { ml: 0.25 },
                      }}
                    >{renderEnrolledAngles(person)}{renderEnrolledVoice(person)}{renderEnrolledActions(person)}</Stack>}
                  />
                );
              })}
            </Box>
          )}
          {/* Pagination controls for enrolled tab */}
          {!loading && pagination.total > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 1.5, sm: 2 },
                mt: 2,
                px: { xs: 1, sm: 2 },
                pb: { xs: 1, sm: 2 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Rows per page:
                </Typography>
                <FormControl size="small">
                  <Select
                    value={pagination.limit}
                    onChange={handleLimitChange}
                    sx={{ minWidth: 80 }}
                  >
                    <SelectMenuItem value={10}>10</SelectMenuItem>
                    <SelectMenuItem value={20}>20</SelectMenuItem>
                    <SelectMenuItem value={50}>50</SelectMenuItem>
                    <SelectMenuItem value={100}>100</SelectMenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min(pagination.offset + 1, pagination.total)} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
                </Typography>
              </Box>
              <Pagination
                count={pagination.total_pages}
                page={pagination.current_page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                size="small"
                sx={{
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                  '& .MuiPagination-ul': {
                    pl: 0,
                    ml: 0,
                  },
                }}
              />
            </Box>
          )}
        </Box>

      </Container>

      <PeopleExportDialog
        open={directoryExportOpen}
        onClose={() => setDirectoryExportOpen(false)}
        title="Vendors"
        exportFilters={{
          include_deleted: showDeleted,
          q: enrolledQuery?.trim() || null,
          person_id: enrolledSearchPersonId || null,
          is_intrusion: false,
          is_vendor: true,
        }}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              confirmDialog.onConfirm();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={restrictedCameraDialog.open}
        onClose={() => {
          if (!restrictedCameraDialog.saving) {
            setRestrictedCameraDialog({ open: false, person: null, selectedIds: [], saving: false });
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ px: 3, py: 2, borderBottom: '1px solid #eef1f6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                bgcolor: '#e8f1fb',
                color: '#3f51b5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Videocam fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Restricted Cameras
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select cameras that this vendor cannot access
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Vendor Name"
              value={restrictedCameraDialog?.person?.name || ''}
              fullWidth
              size="small"
              disabled
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2">
                Activity Cameras ({activityCameras.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setRestrictedCameraDialog((prev) => ({
                      ...prev,
                      selectedIds: activityCameras.map((cam) => cam.id),
                    }))
                  }
                  disabled={restrictedCameraDialog.saving || activityCameras.length === 0}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setRestrictedCameraDialog((prev) => ({ ...prev, selectedIds: [] }))}
                  disabled={restrictedCameraDialog.saving || (restrictedCameraDialog.selectedIds || []).length === 0}
                >
                  Clear
                </Button>
              </Box>
            </Box>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, maxHeight: 360, overflowY: 'auto', background: '#fcfdff' }}>
              {activityCamerasLoading ? (
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">Loading activity cameras...</Typography>
                </Box>
              ) : activityCameras.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No activity cameras available.
                </Typography>
              ) : (
                activityCameras.map((cam) => (
                  <Box
                    key={cam.id}
                    onClick={() =>
                      setRestrictedCameraDialog((prev) => {
                        const selected = new Set(prev.selectedIds || []);
                        if (selected.has(cam.id)) selected.delete(cam.id);
                        else selected.add(cam.id);
                        return { ...prev, selectedIds: Array.from(selected) };
                      })
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      py: 1.1,
                      borderBottom: '1px solid #f2f4f8',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      '&:hover': { backgroundColor: '#f5f8ff' },
                      '&:last-of-type': { borderBottom: 'none' },
                    }}
                  >
                    <Checkbox checked={(restrictedCameraDialog.selectedIds || []).includes(cam.id)} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {cam.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {cam.description || 'No description'}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid #eef1f6', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {`Selected: ${(restrictedCameraDialog.selectedIds || []).length}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setRestrictedCameraDialog({ open: false, person: null, selectedIds: [], saving: false })}
              disabled={restrictedCameraDialog.saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                const personId = String(restrictedCameraDialog?.person?.person_id || '').trim();
                if (!personId) return;
                try {
                  setRestrictedCameraDialog((prev) => ({ ...prev, saving: true }));
                  const ids = Array.isArray(restrictedCameraDialog.selectedIds) ? restrictedCameraDialog.selectedIds : [];
                  await updatePersonCameraAccess(personId, ids);
                  setPersons((prev) =>
                    prev.map((p) =>
                      String(p?.person_id || '') === personId
                        ? { ...p, restricted_camera_ids: [...ids] }
                        : p
                    )
                  );
                  showSnackbar('Restricted cameras updated successfully', 'success');
                  setRestrictedCameraDialog({ open: false, person: null, selectedIds: [], saving: false });
                } catch (error) {
                  console.error('Failed to update restricted cameras:', error);
                  showSnackbar('Failed to update restricted cameras', 'error');
                  setRestrictedCameraDialog((prev) => ({ ...prev, saving: false }));
                }
              }}
              disabled={restrictedCameraDialog.saving}
            >
              {restrictedCameraDialog.saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <PersonProfileSwitchDialog
        open={profileSwitchDialog.open}
        sourceKind="vendor"
        person={profileSwitchDialog.person}
        onClose={() => setProfileSwitchDialog({ open: false, person: null })}
        onSuccess={(message) => {
          showSnackbar(message, 'success');
          loadFaces(pagination.limit, pagination.offset);
        }}
        onError={(message, severity = 'error') => showSnackbar(message, severity)}
      />

      <Dialog
        open={nameEditDialog.open}
        onClose={() => {
          if (!nameEditDialog.saving) closeNameEditDialog();
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            boxShadow: isMobile ? 'none' : '0 10px 36px rgba(15, 23, 42, 0.16)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            py: { xs: 1.5, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          Edit Vendor
          <IconButton
            size="small"
            onClick={closeNameEditDialog}
            disabled={nameEditDialog.saving}
            sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <TextField
              label="Vendor Name"
              value={nameEditDialog.name}
              onChange={(e) => setNameEditDialog((prev) => ({ ...prev, name: e.target.value }))}
              size="small"
              autoFocus
              fullWidth
            />


            {/* Company - editable only when person_id is not numeric */}
            {!isIntegerPersonId(nameEditDialog.person?.person_id || nameEditDialog.personId) ? (
              <FormControl size="small" fullWidth>
                <InputLabel>Company</InputLabel>
                <Select
                  value={nameEditDialog.companyId || ''}
                  onChange={(e) => handleEditDialogCompanyChange(e.target.value)}
                  label="Company"
                  MenuProps={editSelectMenuProps}
                >
                  <SelectMenuItem value="">
                    <em>None</em>
                  </SelectMenuItem>
                  {editDialogCompanies.map((company) => (
                    <SelectMenuItem key={company.company_id} value={company.company_id}>
                      {company.company_name}
                    </SelectMenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Company"
                value={nameEditDialog.person?.company_name || '-'}
                size="small"
                fullWidth
                disabled
              />
            )}

            {/* Branch - editable only when person_id is not numeric */}
            {!isIntegerPersonId(nameEditDialog.person?.person_id || nameEditDialog.personId) ? (
              <FormControl size="small" fullWidth>
                <InputLabel>Branch</InputLabel>
                <Select
                  value={nameEditDialog.branchId || ''}
                  onChange={(e) => handleEditDialogBranchChange(e.target.value)}
                  label="Branch"
                  disabled={!nameEditDialog.companyId}
                  MenuProps={editSelectMenuProps}
                >
                  <SelectMenuItem value="">
                    <em>None</em>
                  </SelectMenuItem>
                  {editDialogBranches.map((branch) => (
                    <SelectMenuItem
                      key={branch.branch_id}
                      value={branch.branch_id}
                      sx={{ whiteSpace: 'normal', lineHeight: 1.3 }}
                    >
                      {branch.branch_address || branch.branch_name}
                    </SelectMenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Branch"
                value={nameEditDialog.person?.branch_address || nameEditDialog.person?.branch_name || '-'}
                size="small"
                fullWidth
                disabled
              />
            )}

            <FormControl size="small" fullWidth>
              <InputLabel>Designation</InputLabel>
              <Select
                value={nameEditDialog.designation || ''}
                onChange={(e) => setNameEditDialog((prev) => ({ ...prev, designation: e.target.value }))}
                label="Designation"
                MenuProps={editSelectMenuProps}
              >
                <SelectMenuItem value="">
                  <em>None</em>
                </SelectMenuItem>
                {editDesignationOptions.map((designation) => (
                  <SelectMenuItem key={designation} value={designation}>
                    {designation}
                  </SelectMenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={nameEditDialog.department || ''}
                onChange={(e) => setNameEditDialog((prev) => ({ ...prev, department: e.target.value }))}
                label="Department"
                MenuProps={editSelectMenuProps}
              >
                <SelectMenuItem value="">
                  <em>None</em>
                </SelectMenuItem>
                {editDepartmentOptions.map((department) => (
                  <SelectMenuItem key={department} value={department}>
                    {department}
                  </SelectMenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 1 }} />

            {/* Custom Training Images Section */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Custom Training Images
            </Typography>

            {/* Preview Selected Images */}
            {customTrainingImages.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {customTrainingImages.map((image, index) => (
                  <Box
                    key={image.id}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                      alignItems: { xs: 'stretch', sm: 'center' },
                    }}
                  >
                    {/* Image Preview */}
                    <Box
                      sx={{
                        position: 'relative',
                        width: { xs: '100%', sm: 80 },
                        height: { xs: 160, sm: 80 },
                        flexShrink: 0,
                        border: '2px solid #e0e0e0',
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={image.previewUrl}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveCustomImage(image.id)}
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          padding: '2px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                          },
                        }}
                      >
                        <Close fontSize="small" sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>

                    {/* Label Input */}
                    <TextField
                      label={`Image ${index + 1} Label (optional)`}
                      placeholder="e.g., With glasses, Side view, Smiling"
                      value={image.label}
                      onChange={(e) => handleUpdateImageLabel(image.id, e.target.value)}
                      size="small"
                      fullWidth
                      disabled={nameEditDialog.saving}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}

            {/* Select Images Button */}
            <input
              ref={customImageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleCustomImageSelect}
            />
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => customImageInputRef.current?.click()}
              fullWidth
              disabled={nameEditDialog.saving}
            >
              Select Images ({customTrainingImages.length} selected)
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={closeNameEditDialog} disabled={nameEditDialog.saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePersonName}
            variant="contained"
            disabled={nameEditDialog.saving}
          >
            {nameEditDialog.saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Image Dialog */}

      <Dialog
        open={editDialog.open}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Vendor Image</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Guided capture takes the photo automatically when the face pose is valid. You can also upload an image.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!editCameraOn && previewUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 8 }}
                />
              </Box>
            )}

            {previewUrl && editQualityResult?.loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Skeleton variant="rounded" height={18} width="46%" animation="wave" />
                <Skeleton variant="rounded" height={14} animation="wave" />
              </Box>
            ) : null}

            {previewUrl && editQualityResult && !editQualityResult.loading ? (
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: editQualityResult.success ? '#ecfdf5' : '#fef2f2',
                  border: `1px solid ${editQualityResult.success ? '#86efac' : '#fecaca'}`,
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>
                  {editQualityResult.message}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Faces: {editQualityResult.face_count ?? 0} | Pose: {editQualityResult.pose_quality ?? 0} | Lighting: {editQualityResult.lighting_quality ?? 0} | Score: {editQualityResult.quality_score ?? 0} | Similarity: {editQualityResult.similarity ?? 'N/A'} | Embedding: {editQualityResult.encoding_dimension ?? 0}
                </Typography>
              </Box>
            ) : null}

            <Button variant="outlined" component="label">
              Choose Image
              <input hidden accept="image/*" type="file" onChange={onFileChange} />
            </Button>

            <Divider>or</Divider>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {!editCameraOn ? (
                <Button
                  variant="contained"
                  startIcon={<Videocam />}
                  onClick={startEditCapture}
                  sx={{ backgroundColor: primary }}
                >
                  Start Capture
                </Button>
              ) : (
                <Button variant="outlined" onClick={() => setEditCameraOn(false)}>
                  Stop Camera
                </Button>
              )}
              {previewUrl && !editCameraOn ? (
                <Button variant="outlined" onClick={startEditCapture}>
                  Retake
                </Button>
              ) : null}
            </Box>

            {editCameraOn && (
              <WebcamCapture
                key={`edit-capture-${editCaptureNonce}`}
                stepKey={editDialog.face?.face_angle || 'front'}
                expectedDirection={directionForFaceAngle(editDialog.face?.face_angle)}
                onCaptured={handleEditAutoCaptured}
                onPermissionDenied={(message) => {
                  if (message) showSnackbar(message, 'error');
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button onClick={submitEdit} variant="contained" disabled={!selectedFile}>Update</Button>
        </DialogActions>
      </Dialog>

      {/* Face Detail Dialog */}
      <Dialog
        open={faceDetailDialog.open}
        onClose={() => setFaceDetailDialog({ ...faceDetailDialog, open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Face Details</DialogTitle>
        <DialogContent sx={{ pl: 2 }}>
          {faceDetailDialog.face && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Face ID"
                value={faceDetailDialog.face.face_id}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Name"
                value={faceDetailDialog.face.name}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Person ID"
                value={faceDetailDialog.face.person_id}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Face Angle"
                value={formatAngleLabel(faceDetailDialog.face.face_angle, faceDetailDialog.face.metadata)}
                helperText={faceDetailDialog.face.face_angle}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Pose Quality"
                value={`${(faceDetailDialog.face.pose_quality * 100).toFixed(1)}%`}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Lighting Quality"
                value={`${(faceDetailDialog.face.lighting_quality * 100).toFixed(1)}%`}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Created At"
                value={formatDate(faceDetailDialog.face.created_at)}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Last Updated"
                value={formatDate(faceDetailDialog.face.last_updated)}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              {faceDetailDialog.face.deleted_at && (
                <TextField
                  fullWidth
                  label="Deleted At"
                  value={formatDate(faceDetailDialog.face.deleted_at)}
                  margin="normal"
                  InputProps={{ readOnly: true }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFaceDetailDialog({ ...faceDetailDialog, open: false })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <EmployeeFacesDialog
        open={Boolean(facesModalPerson)}
        person={facesModalPerson}
        onClose={() => setFacesModalPerson(null)}
        formatAngleLabel={formatAngleLabel}
        getQualityColor={getQualityColor}
         onFaceImageClick={(face) =>
    handleOpenFaceGallery(facesModalPerson, face)
  }
        formatDate={formatDate}
        showDeleted={showDeleted}
        updatedBy={facesModalPerson ? resolveUpdatedByName(facesModalPerson) : ''}
        onEdit={handleOpenEdit}
        onDeactivate={handleSoftDeleteFace}
        onRestore={handleRestoreFace}
      />

      {/* Face Image Dialog */}
      <Dialog
        open={imageDialog.open}
        onClose={() => {
          if (imageDialog.imageUrl) {
            URL.revokeObjectURL(imageDialog.imageUrl);
          }
          setImageDialog({ open: false, imageUrl: null, faceName: '' });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Face Image - {imageDialog.faceName}</DialogTitle>
        <DialogContent>
          {imageDialog.imageUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <img
                src={imageDialog.imageUrl}
                alt={`Face for ${imageDialog.faceName}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '500px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (imageDialog.imageUrl) {
                URL.revokeObjectURL(imageDialog.imageUrl);
              }
              setImageDialog({ open: false, imageUrl: null, faceName: '' });
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Voice Upload Modal */}
      <Dialog
        open={voiceModalOpen}
        onClose={closeVoiceModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px', overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: primary,
          color: 'white',
          py: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VolumeUp />
            <Typography variant="h6" component="span">
              {voiceModalEmployee?.mode === 'update' ? 'Update' : 'Add'} Voice for {voiceModalEmployee?.employeeName || 'Employee'}
            </Typography>
          </Box>
          <IconButton onClick={closeVoiceModal} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2, mt: 2.0 }}>
          {/* IN Voice Section */}
          <Box sx={{
            mb: 3,
            p: 2.5,
            border: '1px solid',
            borderColor: '#e0e0e0',
            borderRadius: '12px',
            backgroundColor: '#fff'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Login sx={{ fontSize: 20, color: '#555' }} />
              IN Voice (Check-in)
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Upload Button */}
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ textTransform: 'none' }}
              >
                Upload WAV
                <input
                  type="file"
                  accept=".wav,audio/wav"
                  hidden
                  onChange={(e) => handleFileUpload(e, 'checkin')}
                />
              </Button>

              <Button
                variant="outlined"
                startIcon={<SmartToy />}
                onClick={() => openAIVoiceDialog('checkin')}
                sx={{ textTransform: 'none' }}
              >
                AI Generate Voice
              </Button>

              {/* Record Button */}
              {!isRecordingCheckin ? (
                <Button
                  variant="outlined"
                  startIcon={<Mic />}
                  onClick={() => startRecording('checkin')}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#d32f2f',
                    color: '#d32f2f',
                    '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211, 47, 47, 0.04)' }
                  }}
                >
                  Record
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Stop />}
                  onClick={() => stopRecording('checkin')}
                  sx={{
                    textTransform: 'none',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
                      '70%': { boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      animation: 'blink 0.8s ease-in-out infinite',
                      '@keyframes blink': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.3 },
                      }
                    }} />
                    Recording...
                  </Box>
                </Button>
              )}
            </Box>

            {/* Recording Animation */}
            {isRecordingCheckin && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 24 }}>
                  {[...Array(5)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 4,
                        backgroundColor: '#d32f2f',
                        borderRadius: 1,
                        animation: `wave 0.8s ease-in-out infinite`,
                        animationDelay: `${i * 0.1}s`,
                        '@keyframes wave': {
                          '0%, 100%': { height: 8 },
                          '50%': { height: 24 },
                        }
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="error">
                  Speak now...
                </Typography>
              </Box>
            )}

            {/* Audio Player */}
            {checkinAudioUrl && !isRecordingCheckin && (
              <Box sx={{ mt: 2.5 }}>
                <AudioPlayer
                  src={checkinAudioUrl}
                  showJumpControls={false}
                  showDownloadProgress={false}
                  showFilledProgress={true}
                  customAdditionalControls={[]}
                  customVolumeControls={[]}
                  layout="horizontal-reverse"
                  style={{
                    borderRadius: '8px',
                    boxShadow: 'none',
                    backgroundColor: '#f5f5f5'
                  }}
                />
              </Box>
            )}
          </Box>

          {/* OUT Voice Section */}
          <Box sx={{
            p: 2.5,
            border: '1px solid',
            borderColor: '#e0e0e0',
            borderRadius: '12px',
            backgroundColor: '#fff'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Logout sx={{ fontSize: 20, color: '#555' }} />
              OUT Voice (Check-out)
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Upload Button */}
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ textTransform: 'none' }}
              >
                Upload WAV
                <input
                  type="file"
                  accept=".wav,audio/wav"
                  hidden
                  onChange={(e) => handleFileUpload(e, 'checkout')}
                />
              </Button>

              <Button
                variant="outlined"
                startIcon={<SmartToy />}
                onClick={() => openAIVoiceDialog('checkout')}
                sx={{ textTransform: 'none' }}
              >
                AI Generate Voice
              </Button>

              {/* Record Button */}
              {!isRecordingCheckout ? (
                <Button
                  variant="outlined"
                  startIcon={<Mic />}
                  onClick={() => startRecording('checkout')}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#d32f2f',
                    color: '#d32f2f',
                    '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211, 47, 47, 0.04)' }
                  }}
                >
                  Record
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Stop />}
                  onClick={() => stopRecording('checkout')}
                  sx={{
                    textTransform: 'none',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      animation: 'blink 0.8s ease-in-out infinite',
                    }} />
                    Recording...
                  </Box>
                </Button>
              )}
            </Box>

            {/* Recording Animation */}
            {isRecordingCheckout && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 24 }}>
                  {[...Array(5)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 4,
                        backgroundColor: '#d32f2f',
                        borderRadius: 1,
                        animation: `wave 0.8s ease-in-out infinite`,
                        animationDelay: `${i * 0.1}s`,
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="error">
                  Speak now...
                </Typography>
              </Box>
            )}

            {/* Audio Player */}
            {checkoutAudioUrl && !isRecordingCheckout && (
              <Box sx={{ mt: 2.5 }}>
                <AudioPlayer
                  src={checkoutAudioUrl}
                  showJumpControls={false}
                  showDownloadProgress={false}
                  showFilledProgress={true}
                  customAdditionalControls={[]}
                  customVolumeControls={[]}
                  layout="horizontal-reverse"
                  style={{
                    borderRadius: '8px',
                    boxShadow: 'none',
                    backgroundColor: '#f5f5f5'
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={closeVoiceModal}
            disabled={voiceUploading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveVoices}
            disabled={voiceUploading || (!checkinVoiceBlob && !checkoutVoiceBlob)}
            startIcon={voiceUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
            sx={{ backgroundColor: primary, '&:hover': { backgroundColor: '#2e3d66' } }}
          >
            {voiceUploading ? 'Uploading...' : 'Save Voices'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={aiVoiceDialogOpen}
        onClose={closeAIVoiceDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          AI Generate {aiVoiceType === 'checkin' ? 'IN' : 'OUT'} Voice
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Key Type: Cartesia API Key
          </Typography>
          <Autocomplete
            freeSolo
            fullWidth
            options={aiVoiceSavedKeys}
            value={aiVoiceSavedKeys.find((key) => key.credential_id === aiVoiceSelectedKeyId) || null}
            inputValue={aiVoiceApiKeyInput}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') {
                setAiVoiceSelectedKeyId('');
                setAiVoiceApiKeyInput(newValue);
                return;
              }
              if (newValue?.credential_id) {
                setAiVoiceSelectedKeyId(newValue.credential_id);
                setAiVoiceApiKeyInput(newValue.credential_hint || '');
                return;
              }
              setAiVoiceSelectedKeyId('');
              setAiVoiceApiKeyInput('');
            }}
            onInputChange={(_, newInputValue, reason) => {
              setAiVoiceApiKeyInput(newInputValue);
              if (reason === 'input') {
                setAiVoiceSelectedKeyId('');
              }
            }}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.credential_hint || option.credential_id)}
            loading={aiVoiceLoadingKeys}
            disabled={aiVoiceGenerating}
            sx={{ mb: 2 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Saved API Key"
                type={aiVoiceSelectedKeyId ? 'text' : 'password'}
                placeholder="Select saved key or enter new API key"
                autoComplete="off"
                inputProps={{ ...params.inputProps, autoComplete: 'off' }}
              />
            )}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Voice Text"
            value={aiVoiceText}
            onChange={(e) => setAiVoiceText(e.target.value)}
            disabled={aiVoiceGenerating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAIVoiceDialog} disabled={aiVoiceGenerating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateAIVoice}
            disabled={aiVoiceGenerating || (!aiVoiceSelectedKeyId && !aiVoiceApiKeyInput.trim())}
            startIcon={aiVoiceGenerating ? <CircularProgress size={16} color="inherit" /> : <SmartToy />}
            sx={{ backgroundColor: primary, '&:hover': { backgroundColor: '#2e3d66' } }}
          >
            {aiVoiceGenerating ? 'Generating...' : 'Generate Voice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Find Person Modal */}
      <Dialog
        open={findPersonModalOpen}
        onClose={handleFindPersonModalClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            boxShadow: isMobile ? 'none' : '0 10px 36px rgba(15, 23, 42, 0.16)',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: { xs: 1.5, sm: 2 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
        }}>
          <Typography variant="inherit" sx={{ fontWeight: 600 }}>Find Person in Logs</Typography>
          <IconButton
            size="small"
            onClick={handleFindPersonModalClose}
            disabled={findPersonLoading}
            sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          {findPersonModalPerson && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Person Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Employee
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {findPersonModalPerson.name} ({findPersonModalPerson.person_id})
                </Typography>
              </Box>

              {/* Select which enrolled face image to use for the search */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Select face for search
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={selectedAngle}
                  onChange={(e, newVal) => {
                    if (newVal != null) setSelectedAngle(String(newVal));
                  }}
                  disabled={findPersonLoading}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    py: 0.5,
                    '& .MuiToggleButtonGroup-grouped': {
                      margin: 0,
                      borderRadius: '12px !important',
                      border: '2px solid !important',
                    },
                  }}
                >
                  {findPersonModalPerson.faces
                    .filter((f) => !f.deleted_at)
                    .map((face) => {
                      const faceIdStr = String(face.face_id);
                      const isSelected = String(selectedAngle) === faceIdStr;
                      const thumb = findPersonFaceThumbUrls[faceIdStr];
                      const angleLabel = formatAngleLabel(face.face_angle, face.metadata || {});
                      return (
                        <ToggleButton
                          key={faceIdStr}
                          value={faceIdStr}
                          aria-label={angleLabel}
                          aria-pressed={isSelected}
                          sx={{
                            position: 'relative',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 1.5,
                            minWidth: { xs: 112, sm: 128 },
                            maxWidth: { xs: 140, sm: 148 },
                            textTransform: 'none',
                            borderColor: 'divider !important',
                            bgcolor: 'background.paper',
                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                            '&:hover': {
                              borderColor: 'rgba(100, 116, 139, 0.9) !important',
                              bgcolor: 'action.hover',
                            },
                            '&.Mui-selected': {
                              borderColor: `${primary} !important`,
                              bgcolor: 'rgba(64, 81, 138, 0.1) !important',
                              boxShadow: `0 0 0 3px rgba(64, 81, 138, 0.28), 0 6px 16px rgba(15, 23, 42, 0.1)`,
                            },
                            '&.Mui-selected:hover': {
                              bgcolor: 'rgba(64, 81, 138, 0.14) !important',
                              borderColor: `${primary} !important`,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: { xs: 88, sm: 100 },
                              height: { xs: 88, sm: 100 },
                              borderRadius: 1.5,
                              overflow: 'hidden',
                              bgcolor: '#eef2f6',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid rgba(148, 163, 184, 0.35)',
                            }}
                          >
                            {thumb ? (
                              <Box
                                component="img"
                                src={thumb}
                                alt={angleLabel}
                                sx={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain',
                                  objectPosition: 'center center',
                                  display: 'block',
                                }}
                              />
                            ) : thumb === '' ? (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  px: 0.5,
                                  textAlign: 'center',
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  No preview
                                </Typography>
                              </Box>
                            ) : (
                              <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              maxWidth: 120,
                              textAlign: 'center',
                              lineHeight: 1.3,
                              color: isSelected ? primary : 'text.primary',
                              letterSpacing: 0.02,
                            }}
                          >
                            {angleLabel}
                          </Typography>
                        </ToggleButton>
                      );
                    })}
                </ToggleButtonGroup>
              </Box>

              <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
                <Typography component="legend" variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>
                  Find person within
                </Typography>
                <RadioGroup
                  row={!isMobile}
                  value={findPersonLogType}
                  onChange={(e) => setFindPersonLogType(e.target.value)}
                >
                  <FormControlLabel
                    value="audit"
                    control={<Radio size="small" />}
                    label="In/Out logs"
                    disabled={findPersonLoading}
                  />
                  <FormControlLabel
                    value="activity"
                    control={<Radio size="small" />}
                    label="Activity logs"
                    disabled={findPersonLoading}
                  />
                </RadioGroup>
              </FormControl>

              {/* Similarity Threshold */}
              <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: { xs: 1.25, sm: 3 } }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    minWidth: 'fit-content',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    lineHeight: { xs: 1.4, sm: '40px' },
                  }}
                >
                  Similarity Threshold: {parseFloat(similarityThreshold || 0).toFixed(2)}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0, maxWidth: { xs: '100%', sm: 300 }, mt: { xs: 0, sm: 0.5 } }}>
                  <Slider
                    value={parseFloat(similarityThreshold || 0.3)}
                    onChange={(e, newValue) => setSimilarityThreshold(String(newValue))}
                    min={0}
                    max={1}
                    step={0.01}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 0.25, label: '0.25' },
                      { value: 0.5, label: '0.5' },
                      { value: 0.75, label: '0.75' },
                      { value: 1, label: '1' },
                    ]}
                    disabled={findPersonLoading}
                    sx={{
                      width: '100%',
                      '& .MuiSlider-thumb': {
                        width: { xs: 14, sm: 10 },
                        height: { xs: 14, sm: 10 },
                      },
                      '& .MuiSlider-markLabel': {
                        fontSize: { xs: '0.75rem', sm: '0.65rem' },
                        top: { xs: 26, sm: 20 },
                      },
                      '& .MuiSlider-track, & .MuiSlider-rail': {
                        height: { xs: 5, sm: 4 },
                      },
                      '& .MuiSlider-mark': {
                        top: '50%',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, borderTop: '1px solid', borderColor: 'divider', gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={handleFindPersonModalClose}
            disabled={findPersonLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleFindPersonSubmit}
            disabled={findPersonLoading || !selectedAngle}
            startIcon={findPersonLoading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: primary, '&:hover': { backgroundColor: '#2e3d66' } }}
          >
            {findPersonLoading ? 'Processing...' : 'Find Person'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Validity Dialog */}
      <Dialog
        open={validityDialog.open}
        onClose={() => { if (!validityDialog.saving) closeValidityDialog(); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Set Employee Validity</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.5, '&&': { pt: 2.5 } }}>
          <TextField
            size="small"
            label="Employee Name"
            value={validityDialog.person?.name || '-'}
            InputProps={{ readOnly: true }}
          />
          <TextField size="small"
            label="Employee ID"
            value={validityDialog.person?.person_id || '-'}
            InputProps={{ readOnly: true }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!validityDialog.unlimited}
                onChange={(e) => setValidityDialog((prev) => ({ ...prev, unlimited: e.target.checked }))}
                disabled={validityDialog.saving}
              />
            }
            label="Unlimited Validity"
          />
          <TextField
            size="small"
            label="Validity (start → end)"
            value={
              validityDialog.unlimited
                ? ''
                : (() => {
                  const vf = validityDialog.validFrom;
                  const vt = validityDialog.validTill;
                  if (!vf && !vt) return 'Select date time range';
                  try {
                    const s = vf ? new Date(vf) : null;
                    const e = vt ? new Date(vt) : null;
                    if (s && e && !Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime())) {
                      return `${format(s, 'MMM d, yyyy h:mm a')} → ${format(e, 'MMM d, yyyy h:mm a')}`;
                    }
                  } catch {
                    /* ignore */
                  }
                  return [vf, vt].filter(Boolean).join(' → ') || 'Click calendar to set';
                })()
            }
            placeholder="Select range"
            onClick={() => {
              if (!validityDialog.unlimited && !validityDialog.saving) setValidityPickerOpen(true);
            }}
            disabled={validityDialog.unlimited || validityDialog.saving}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    disabled={validityDialog.unlimited || validityDialog.saving}
                    onClick={() => setValidityPickerOpen(true)}
                    aria-label="Open validity calendar"
                  >
                    <CalendarMonth fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { cursor: validityDialog.unlimited ? 'default' : 'pointer' },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeValidityDialog} disabled={validityDialog.saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveValidity} disabled={validityDialog.saving}>
            {validityDialog.saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validity: reuse same DateRangePicker + times as elsewhere in this file */}
      <Dialog
        open={validityPickerOpen}
        onClose={() => setValidityPickerOpen(false)}
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            overflow: 'auto',
            width: 'fit-content',
            maxWidth: 'calc(100vw - 24px)',
            m: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1, py: 1.5 }}>
          <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
            Select validity range
          </Typography>
          <IconButton size="small" onClick={() => setValidityPickerOpen(false)} aria-label="Close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, width: 'fit-content', maxWidth: '100%', overflowX: 'auto' }}>
          <DateRangePickerContent
            dateRange={validityDateRange}
            onDateRangeChange={setValidityDateRange}
            disableMaxDate
            showTimeFields
            startTime={validityStartTime}
            endTime={validityEndTime}
            onStartTimeChange={setValidityStartTime}
            onEndTimeChange={setValidityEndTime}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              const today = new Date();
              setValidityDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
              setValidityStartTime('00:00');
              setValidityEndTime('23:59');
            }}
            sx={{ textTransform: 'none' }}
          >
            Reset
          </Button>
          <Button variant="outlined" onClick={() => setValidityPickerOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={applyValidityRangePicker}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <SimilarityThresholdDialog
        open={similarityThresholdDialog.open}
        person={similarityThresholdDialog.person}
        onClose={() => setSimilarityThresholdDialog({ open: false, person: null })}
        onSaved={handleSimilarityThresholdSaved}
        showSnackbar={showSnackbar}
      />

    </Box>
  );
};

export default Vendors;
